import { create } from 'zustand';
import { debounce } from 'lodash';

import { spaceCRDTService } from '../services/crdt/spaceCRDTService';
import { spaceService } from '../services/spaceService';
import { useSpatialUIStore } from './spaceSpatialUIStore';
import { AnyWindowEntity, WindowEntity } from '../types/windows';

import { createFileLogger, formatJSON } from '../utils/logger';
import { modelService } from 'services/modelService';
import { SpaceSettings, SpaceBackground, UISpace as Space } from '../types/spaces';
import { DEFAULT_NEW_SPACE_BACKGROUND } from '../constants/spaceBackgrounds';
import { createSpaceActions } from './spaceActions';
import { getWindowEntityId, layoutFamilyOf, computeCascadeLayout } from '../components/Space/utils/windowUtils';
import {
  normalizeWindowTabOrder,
  windowTabOrderEquals,
} from '../components/Space/utils/windowTabOrder';
import { entityActions } from '../stores/entityActions';
import { entityRegistry } from '../stores/entityRegistry';
import { useGlobalEntityStore } from '../stores/globalEntityStore';
import { upsertEntity } from '../stores/entityMutations';
import { useEntityByIdCache } from '../stores/entityByIdCache';
// Add this type declaration for the global lock
declare global {
  interface Window {
    __SPACE_INITIALIZING__?: boolean;
  }
}

// Add a timestamp marker to track code loading
const log = createFileLogger('SPACE_STORE', { enabled: false });
log.info(`Code loaded with direct persistence at ${new Date().toISOString()}`);

// Unified backend save queue to ensure consistent ordering
const backendSaveQueue = {
  pending: false,
  // Set when a save is requested while another is already in flight. The
  // in-flight save reads a snapshot of state when it started, so a request that
  // arrives mid-flight would otherwise be silently dropped. `dirty` forces a
  // follow-up save once the current one settles, guaranteeing the latest state
  // (e.g. a just-toggled isChatVisible) reaches the backend.
  dirty: false,
  timer: null as NodeJS.Timeout | null,
  
  schedule: (delayMs: number = 500) => {
    if (backendSaveQueue.timer) {
      clearTimeout(backendSaveQueue.timer);
    }
    
    backendSaveQueue.timer = setTimeout(() => {
      backendSaveQueue.execute();
    }, delayMs);
  },
  
  execute: async () => {
    if (backendSaveQueue.pending) {
      // A save is already running — mark dirty so we re-save with the latest
      // state once it finishes, instead of dropping this request.
      backendSaveQueue.dirty = true;
      return;
    }
    
    backendSaveQueue.pending = true;
    backendSaveQueue.dirty = false;
    backendSaveQueue.timer = null;
    
    try {
      const state = useSpaceStore.getState();
      const spaceId = state.activeSpace?.id;
      
      if (spaceId) {
        await state.saveToBackend();
        // Backend save completed (log removed to reduce spam)
      }
    } catch (error) {
      log.error('Backend save failed:', error);
    } finally {
      backendSaveQueue.pending = false;
      // If a save was requested while this one was running, run it now with the
      // freshest state.
      if (backendSaveQueue.dirty) {
        backendSaveQueue.dirty = false;
        backendSaveQueue.schedule(50);
      }
    }
  },
  
  flush: () => {
    if (backendSaveQueue.timer) {
      clearTimeout(backendSaveQueue.timer);
      backendSaveQueue.timer = null;
    }
    return backendSaveQueue.execute();
  }
};

// Create debounced save for drag operations specifically
const debouncedPersistStore = debounce(() => {
  log.info('Saving to backend after drag debounce');
  backendSaveQueue.execute();
}, 500); // 500ms debounce for drag operations

// Debounced localStorage write for the active-window preference.
// Sits on the focus hot path — fire-and-forget, delay is fine.
const debouncedSaveActiveWindowId = debounce(
  (spaceId: string, windowId: string) => {
    try { localStorage.setItem(`activeWindowId_${spaceId}`, windowId); } catch { /* quota */ }
  },
  400,
);

export type SpaceStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface WindowState {
  position: { x: number; y: number };
  size: { width: number; height: number };
}

// ✅ NORMALIZED: Window with entity references only (no embedded entities)
// entityId + entity registry is the single source of truth for entity data
export interface NormalizedWindowEntity extends WindowEntity {
  entityId?: string; // Reference to entity ID (_id field)
  entityLlmId?: string; // Reference to entity llmId for fast lookup
}

export interface SpaceState {
  status: SpaceStatus;
  error?: Error;
  activeSpace: Space | null;
  windows: Record<string, NormalizedWindowEntity>; // ✅ SURGICAL FIX: Now uses normalized windows
  // ✅ REFACTOR: entities removed - use the entity registry as the single source of truth
  contextEntities: Record<string, { llmId: string; windowId?: string; isManuallySet: boolean; addedAt: number }>; // ✅ NEW: Persistent context state
  contextExcerpts: any[]; // ✅ NEW: Persistent excerpts
  selectedIds: string[];
  activeWindowId: string | null;
  layout?: any;
  cleanupFn: () => void;
  initializing: boolean;
  currentSpaceId?: string;
  initializationStatus: 'idle' | 'initializing' | 'initialized' | 'error';
  isChatVisible: boolean;
  loadedWindowIds: Set<string>;  // ✅ PERFORMANCE: Track which windows have been mounted (lazy loading)
  
  // Global page context awareness
  pageType: 'home' | 'space' | 'standalone' | null;
  isStandaloneMode: boolean;
  activeAppId: string | null; // NEW: Track which app is active in standalone mode (for dynamic tool loading)

  // App Focus Mode — URL-derived, transient. Set by useAppFocusMode hook from ?appFocus= param.
  // Never persisted to the backend. Gates chrome-strip in GUILayout / SystemState / FocusedLayout.
  appFocusMode: { appId: string; view?: string } | null;
  setAppFocusMode: (mode: { appId: string; view?: string } | null) => void;
  
  // Sidebar state
  isSidebarVisible: boolean;
  sidebarWidth: number;
  sidebarMode: 'normal' | 'overlay';

  // Desktop shell UI state (persisted per-space)
  desktopSidebarOpen: boolean;
  desktopShowRoot: boolean;
  // Transient (not persisted): remembers desktopSidebarOpen across a focused-mode
  // visit so it can be force-closed on entry and restored on exit. `null` when
  // not currently inside a focused-mode visit.
  desktopSidebarOpenBeforeFocus: boolean | null;
  
  // Chat expand state
  isChatExpanded: boolean;
  previousLayoutState: {
    sidebarVisible: boolean;
    sidebarWidth: number;
  } | null;
  
  // Dev features toggle
  hideDevFeatures: boolean;
  
  // Internal state for triggering updates
  _refreshTimestamp?: number;
  
  // Actions
  setIsChatVisible: (isChatVisible: boolean) => Promise<void>;
  setSidebarVisible: (isVisible: boolean) => Promise<void>;
  setSidebarWidth: (width: number) => Promise<void>;
  setSidebarMode: (mode: 'normal' | 'overlay') => Promise<void>;
  setDesktopSidebarOpen: (open: boolean) => Promise<void>;
  setDesktopShowRoot: (showRoot: boolean) => Promise<void>;
  toggleChatExpanded: () => Promise<void>;
  toggleHideDevFeatures: () => void;
}

export interface SpaceStore extends SpaceState {
  // Internal mutation helpers (single code path for all mutations)
  _applyLocalWindowUpdate: (windowId: string, updates: Partial<NormalizedWindowEntity>, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt';
    syncCRDT?: boolean;
  }) => void;
  _applyLocalWindowDelete: (windowId: string, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt';
    syncCRDT?: boolean;
    nextActiveWindowId?: string | null;
  }) => void;
  
  // Window actions (canonical mutation API)
  actions: {
    createWindow: (params: {
      appType: string;
      title?: string;
      entity?: any;
      position?: { x: number; y: number };
      size?: { width: number; height: number };
      applicationState?: any;
      props?: Record<string, any>;
    }) => Promise<string>;
    updateWindow: (windowId: string, updates: Partial<NormalizedWindowEntity>) => Promise<void>;
    closeWindow: (windowId: string) => void;
    setActiveWindow: (windowId: string | null) => void;
    navigateWindow: (windowId: string, entityId: string) => Promise<void>;
    navigateBack: (windowId: string) => Promise<void>;
    navigateForward: (windowId: string) => Promise<void>;
    clearWindowEntityId: (windowId: string, fallbackTitle?: string) => void;
  };
  
  getSpaceStatus: () => Promise<SpaceStatus>;
  initialize: (spaceId: string, prefetchedSpace?: Space) => Promise<void>;
  setPageContext: (pageType: 'home' | 'space' | 'standalone', spaceId?: string, appId?: string) => void;
  setActiveApp: (appId: string | null) => void; // NEW: Explicitly set active app for tool loading
  addWindow: (window: WindowEntity) => void;
  removeWindow: (windowId: string) => void;
  updateWindow: (windowId: string, updates: Partial<AnyWindowEntity>) => void;
  setWindowZIndex: (windowId: string, zIndex: number) => void;
  selectWindow: (windowId: string, isMultiSelect: boolean) => void;
  groupSelected: () => void;
  ungroup: (windowId: string) => void;
  setWindows: (windows: AnyWindowEntity[]) => void;
  setStatus: (status: SpaceStatus) => void;
  cleanup: () => void;
  updateSpaceName: (newName: string, targetSpaceId?: string) => Promise<void>;
  setBackground: (background: SpaceBackground) => Promise<void>;
  showDebugger: boolean;
  toggleDebugger: () => void;
  setActiveWindow: (windowId: string | null) => void;
  bringWindowToFront: (windowId: string) => void;
  setViewMode: (mode: 'spatial' | 'focused' | 'gui2' | 'home' | 'canvas' | 'spatialCanvas') => Promise<void>;
  applyLayoutForViewMode: (prevViewMode: string | undefined, nextViewMode: string) => void;
  enterFocusedMode: (windowId: string) => Promise<void>;
  exitFocusedMode: () => Promise<void>;
  saveToBackend: () => Promise<void>;
  updateSpaceChatSettings: (spaceChatUpdates: Partial<NonNullable<SpaceSettings['spaceChat']>>) => Promise<void>;
  updateEntitySidebarSettings: (sidebarSettings: Partial<NonNullable<SpaceSettings['entitySidebar']>>) => Promise<void>;
  
  // Entity access
  refreshWindowEntityFromBackend: (windowId: string) => Promise<void>;
  getWorkspaceEntities: () => Array<{ llmId: string; name: string; type: string; windowId: string; entity: any }>;
  
  // ✅ SURGICAL FIX: New normalized entity access methods
  getWindowEntity: (windowId: string) => any | null; // Get entity for a specific window
  getWindowWithEntity: (windowId: string) => AnyWindowEntity | null; // Backward compatibility - returns window with embedded entity
  getAllWindowsWithEntities: () => Record<string, AnyWindowEntity>; // Backward compatibility - returns all windows with embedded entities
  // ✅ REFACTOR: setEntity removed - the entity registry is the single source of truth
  updateEntityEverywhere: (entityId: string, updates: Partial<any>) => Promise<any>; // NEW: Update entity across all stores and components
  fixWindowEntityAssociation: (windowId: string) => Promise<void>; // Fix broken entity associations
  fixAllWindowEntityAssociations: () => Promise<void>; // Fix all broken entity associations
  
  // ✅ NEW: Context management methods
  addToContext: (llmId: string, isManuallySet?: boolean) => void; // Add entity to persistent context
  removeFromContext: (llmId: string) => void; // Remove entity from persistent context
  getContextEntities: () => Record<string, { llmId: string; isManuallySet: boolean; addedAt: number }>; // Get current context state
  syncContextWithContextStore: () => void; // Sync with contextStore
  
  // ✅ PERFORMANCE: Lazy window loading methods
  markWindowAsLoaded: (windowId: string) => void; // Mark window as loaded (mounted)
  markWindowsAsLoaded: (windowIds: string[]) => void; // Mark many windows loaded in one state update
  getLoadedWindowIds: () => Set<string>; // Get all loaded window IDs
  // Excerpts persistence
  addExcerpt: (excerpt: any) => void;
  removeExcerpt: (index: number) => void;
  clearExcerpts: () => void;
  removeContextEntity: (llmId: string) => void;
  
  // Cleanup methods
  cleanupStaleWindows: () => Promise<void>;
  forceSyncCRDTWithLocalState: () => Promise<void>;
  
  // New hierarchical operations
  moveWindowToGroup: (windowId: string, targetGroupId: string | null, position: 'above' | 'below' | 'inside') => Promise<void>;
  createGroupFromWindows: (windowIds: string[], groupName?: string) => Promise<string | null>;
  ungroupWindows: (groupId: string) => Promise<boolean>;
  updateWindowOrder: (windowIds: string[], parentGroupId?: string | null) => Promise<void>;
  updateGroupCollapsedState: (groupId: string, isCollapsed: boolean) => Promise<void>;

  // Tab ordering
  reorderWindowTabs: (orderedIds: string[]) => void;
}

export const useSpaceStore = create<SpaceStore>((set, get) => {
  /**
   * Internal helper: Apply local window update (create or update)
   * This is the SINGLE code path for all window mutations
   */
  const _applyLocalWindowUpdate = (
    windowId: string,
    updates: Partial<NormalizedWindowEntity>,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt';
      syncCRDT?: boolean;
    }
  ): void => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    // ✅ RUNTIME GUARDS: Prevent critical bugs
    
    // 🔴 CRITICAL GUARD: Prevent CRDT infinite loop (browser crash)
    // CRDT updates MUST NOT sync back to CRDT (creates infinite loop)
    if (options.source === 'crdt' && options.syncCRDT !== false) {
      const errorMsg = `[spaceStore] 🔄 CRDT LOOP PREVENTED! CRDT updates must set syncCRDT=false. Window: ${windowId}`;
      console.error(errorMsg, {
        windowId,
        source: options.source,
        syncCRDT: options.syncCRDT,
        stack: new Error().stack
      });
      // THROW to prevent infinite loop - this is critical
      throw new Error(errorMsg);
    }
    
    // Guard: Window should have required fields
    if (!windowId) {
      console.error('[spaceStore] ⚠️ RUNTIME GUARD: windowId is required');
      throw new Error('[spaceStore] windowId is required for window update');
    }
    
    const currentState = get();
    const existingWindow = currentState.windows[windowId];
    const spaceId = currentState.activeSpace?.id;
    
    // PERFORMANCE: Check if updates would actually change anything BEFORE merging
    if (existingWindow) {
      let hasRealChanges = false;
      for (const [key, value] of Object.entries(updates)) {
        const existingValue = (existingWindow as any)[key];
        
        // Deep equality for objects like position and size
        if (typeof value === 'object' && value !== null && typeof existingValue === 'object' && existingValue !== null) {
          // Fast numeric compare for position {x, y} and size {width, height} — avoids JSON.stringify on the hot path.
          if ('x' in value && 'y' in value) {
            if ((value as any).x !== (existingValue as any).x || (value as any).y !== (existingValue as any).y) {
              hasRealChanges = true;
              break;
            }
          } else if ('width' in value && 'height' in value) {
            if ((value as any).width !== (existingValue as any).width || (value as any).height !== (existingValue as any).height) {
              hasRealChanges = true;
              break;
            }
          } else {
            if (JSON.stringify(value) !== JSON.stringify(existingValue)) {
              hasRealChanges = true;
              break;
            }
          }
        } else if (existingValue !== value) {
          hasRealChanges = true;
          break;
        }
      }
      
      // If nothing actually changed, skip the entire update
      if (!hasRealChanges) {
        return;
      }
    }
    
    // Merge with existing window or create new; stamp updatedAt for CRDT
    // stale-overwrite guard so a late remote snapshot never reverts a local drag.
    const updatedWindow: NormalizedWindowEntity = existingWindow
      ? { ...existingWindow, ...updates, id: windowId, updatedAt: Date.now() }
      : { ...updates, id: windowId, updatedAt: Date.now() } as NormalizedWindowEntity;

    // Debug: log title changes
    if (updates.title !== undefined && existingWindow?.title !== updates.title) {
      log.log(JSON.stringify({
        event: '[TITLE_DEBUG] _applyLocalWindowUpdate:titleChange',
        windowId,
        appType: (updates as any).appType ?? existingWindow?.appType ?? null,
        entityId: (updates as any).entityId ?? existingWindow?.entityId ?? null,
        oldTitle: existingWindow?.title ?? null,
        newTitle: updates.title,
        source: options.source,
        syncCRDT: options.syncCRDT,
        callerStack: new Error().stack?.split('\n').slice(1, 5).join(' | ') ?? null,
      }));
    }

    // Route backfill: if this window has no route yet, derive one from legacy
    // fields so the per-window router can always read a consistent route.
    // This runs on every pass-through including CRDT loads and REST seeds.
    if (!(updatedWindow as any).route && (updatedWindow as any).appType) {
      const appType = (updatedWindow as any).appType as string;
      const eid = (updatedWindow as any).entityId as string | undefined;
      const elid = (updatedWindow as any).entityLlmId as string | undefined;
      const mode = (updatedWindow as any).props?.renderMode as string | undefined;
      (updatedWindow as any).route = {
        appType,
        params: {
          ...(eid ? { entityId: eid } : {}),
          ...(elid ? { entityLlmId: elid } : {}),
          ...(mode ? { mode } : {}),
        },
      };
      if (!(updatedWindow as any).routeHistory) {
        (updatedWindow as any).routeHistory = [(updatedWindow as any).route];
        (updatedWindow as any).routeIndex = 0;
      }
    }
    
    // Mirror position/size/zIndex into the active layout-family slice so each mode
    // retains its own independent geometry. Only applies to local writes in
    // spatial-family modes; CRDT/remote loads skip this to avoid stomping a saved
    // arrangement with a stale snapshot from the other mode.
    if (options.source === 'local') {
      const fam = layoutFamilyOf(get().activeSpace?.settings?.viewMode);
      if (fam && (updates.position !== undefined || updates.size !== undefined || updates.zIndex !== undefined)) {
        const prevSlice = existingWindow?.layouts?.[fam];
        updatedWindow.layouts = {
          ...existingWindow?.layouts,
          [fam]: {
            position: updatedWindow.position ?? prevSlice?.position ?? { x: 0, y: 0 },
            size: updatedWindow.size ?? prevSlice?.size ?? { width: 760, height: 560 },
            zIndex: updates.zIndex ?? prevSlice?.zIndex ?? updatedWindow.zIndex,
          },
        };
      }
    }

    // Update state (we already checked for changes above)
    set((state) => ({
      windows: {
        ...state.windows,
        [windowId]: updatedWindow
      }
    }));
    
    // Only sync to CRDT if it's a local action
    if (options.syncCRDT && options.source === 'local' && spaceId) {
      if ((updatedWindow as any).title !== existingWindow?.title) {
        log.log(JSON.stringify({
          event: '[TITLE_DEBUG] _applyLocalWindowUpdate:crdtWrite',
          windowId,
          spaceId,
          oldTitle: existingWindow?.title ?? null,
          newTitle: (updatedWindow as any).title ?? null,
        }));
      }
      // ✅ CRITICAL FIX: Update CRDT immediately to prevent race condition
      // The CRDT observer deletes windows that exist in store but not in CRDT,
      // so we must sync to CRDT before any CRDT sync event can trigger
      spaceCRDTService.updateWindow(spaceId, windowId, updatedWindow);
      
      // Also persist to backend
      const isNewWindow = !existingWindow;
      if (isNewWindow) {
        // Persist immediately for new windows
        backendSaveQueue.flush();
      } else {
        // For updates, use delayed batch save
        backendSaveQueue.schedule(500);
      }
    }
  };
  
  /**
   * Internal helper: Apply local window delete
   * This is the SINGLE code path for all window deletions
   */
  const _applyLocalWindowDelete = (
    windowId: string,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt';
      syncCRDT?: boolean;
      nextActiveWindowId?: string | null;
    }
  ): void => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    const currentState = get();
    const spaceId = currentState.activeSpace?.id;

    // Capture the filtered tab order before the set() so we can broadcast it
    // to CRDT afterward. We compute it here rather than inside set() because
    // Zustand's updater runs synchronously but the CRDT call must be outside it.
    const existingTabOrder = currentState.activeSpace?.settings?.windowTabOrder;
    const filteredTabOrder = existingTabOrder
      ? existingTabOrder.filter(id => id !== windowId)
      : null;

    if (options.syncCRDT && options.source === 'local' && spaceId) {
      spaceCRDTService.removeWindow(spaceId, windowId);
      backendSaveQueue.schedule(500);
    }
    
    set((state) => {
      const { [windowId]: removed, ...remainingWindows } = state.windows;
      
      const newLoadedSet = new Set(state.loadedWindowIds);
      newLoadedSet.delete(windowId);
      
      const isActiveBeingDeleted = state.activeWindowId === windowId;
      let newActiveWindowId = state.activeWindowId;
      if (isActiveBeingDeleted) {
        newActiveWindowId = options.nextActiveWindowId !== undefined
          ? options.nextActiveWindowId
          : null;
      }

      // Keep windowTabOrder in sync with the removed window.
      const currentOrder = state.activeSpace?.settings?.windowTabOrder;
      const newActiveSpace = (state.activeSpace && currentOrder)
        ? { ...state.activeSpace, settings: { ...state.activeSpace.settings, windowTabOrder: currentOrder.filter(id => id !== windowId) } }
        : state.activeSpace;

      return {
        windows: remainingWindows,
        loadedWindowIds: newLoadedSet,
        activeWindowId: newActiveWindowId,
        activeSpace: newActiveSpace,
      };
    });

    // Broadcast the updated tab order to CRDT so other sessions/tabs converge
    // on the clean list. The window itself is removed via spaceCRDTService.removeWindow
    // above, but windowTabOrder is stored in the settings map and must be updated
    // separately — it is NOT derived from the windows map on read.
    if (options.syncCRDT && options.source === 'local' && spaceId && filteredTabOrder) {
      spaceCRDTService.updateSettings(spaceId, { windowTabOrder: filteredTabOrder });
    }
  };
  
  return {
  status: 'idle',
  activeSpace: null,
  windows: {},
  // ✅ REFACTOR: entities removed - use the entity registry as the single source of truth
  contextEntities: {}, // ✅ NEW: Persistent context state - tracks which entities are in context
  contextExcerpts: [],
  selectedIds: [],
  initializing: false,
  activeWindowId: null,
  layout: undefined,
  cleanupFn: () => {},
  currentSpaceId: undefined,
  initializationStatus: 'idle',
  showDebugger: false,
  isChatVisible: true, // ✅ Default to visible for new users
  loadedWindowIds: new Set<string>(),  // ✅ PERFORMANCE: Track mounted windows for lazy loading
  
  // Global page context awareness
  pageType: null,
  isStandaloneMode: false,
  activeAppId: null, // NEW: Track which app is active in standalone mode (for dynamic tool loading)
  appFocusMode: null,
  setAppFocusMode: (mode) => set({ appFocusMode: mode }),
  
  // Sidebar state
  isSidebarVisible: false, // Default to closed
  sidebarWidth: 300,
  sidebarMode: 'normal',

  // Desktop shell UI state
  desktopSidebarOpen: true,
  desktopShowRoot: false,
  desktopSidebarOpenBeforeFocus: null,
  
  // Chat expand state
  isChatExpanded: false,
  previousLayoutState: null,
  
  // Dev features toggle - start with false to show dev features by default in development
  hideDevFeatures: false,
  
  // Set page context from anywhere (SystemContext, layouts, etc.)
  setPageContext: (pageType, spaceId, appId) => {
    const currentState = get();
    
    log.log('[spaceStore.setPageContext] Setting page context:', {
      pageType,
      spaceId,
      appId,
      previousAppId: currentState.activeAppId,
      willClearAppId: !appId && pageType !== 'standalone'
    });
    
    set((state) => ({
      ...state,
      pageType,
      isStandaloneMode: pageType === 'standalone',
      currentSpaceId: spaceId ?? state.currentSpaceId,
      // ✅ IMPORTANT: Only update activeAppId if explicitly provided
      // This preserves app context across route changes
      activeAppId: appId !== undefined ? appId : state.activeAppId,
    }));
    
    log.log('[spaceStore.setPageContext] ✅ Page context updated:', {
      activeAppId: get().activeAppId,
      isStandaloneMode: get().isStandaloneMode
    });
  },
  
  // NEW: Explicitly enable/disable app tools
  setActiveApp: (appId) => {
    log.log('[spaceStore.setActiveApp] Setting active app:', { appId });
    set({ activeAppId: appId });
  },

  // Actions
  setIsChatVisible: async (isChatVisible: boolean) => {
    const currentState = get();
    // No-op if value unchanged — avoids a PATCH /api/spaces/{id} on every page load
    if (currentState.isChatVisible === isChatVisible) return;

    const spaceId = currentState.activeSpace?.id;
    const activeSpace = currentState.activeSpace;
    const newSettings = activeSpace
      ? { ...activeSpace.settings, isChatVisible }
      : undefined;

    // Update local state + activeSpace.settings SYNCHRONOUSLY. This must not be
    // gated on the backend round-trip: the CRDT mirror and the backend save
    // queue both read activeSpace.settings, and loadSpace prefers the CRDT
    // value on reload. If we only updated these after a successful PUT (as the
    // old code did), a failed/deduplicated PUT left the CRDT and backend both
    // holding the stale value — so the chat reappeared after refresh.
    set((state) => ({
      isChatVisible,
      activeSpace: state.activeSpace && newSettings
        ? { ...state.activeSpace, settings: newSettings }
        : state.activeSpace,
    }));

    if (!spaceId || !activeSpace || !newSettings) return;

    // Mirror to the per-user CRDT synchronously — this is the authoritative
    // source on reload, so it must happen independently of the backend PUT.
    spaceCRDTService.updateSettings(spaceId, newSettings);

    // Keep the systemStore cache (recentSpaces) in sync.
    try {
      const useSystemStore = (await import('./systemStore')).default;
      useSystemStore.getState()._applyLocalSpaceUpdate(
        { ...activeSpace, settings: newSettings, updatedAt: new Date().toISOString() },
        { source: 'local', log: false },
      );
    } catch (error) {
      log.error('[SpaceStore] Error syncing isChatVisible to systemStore cache:', error);
    }

    // Persist to the shared backend row via the save queue. The queue re-reads
    // activeSpace.settings at execute time and is non-lossy (re-runs if a save
    // was already in flight), so this change cannot be dropped by a concurrent
    // save the way a direct, deduplicated updateSpace call could be.
    backendSaveQueue.schedule(300);
  },

  toggleHideDevFeatures: () => set(state => ({ hideDevFeatures: !state.hideDevFeatures })),

  getSpaceStatus: async () => {
    return get()?.status;
  },

  initialize: async (spaceId: string, prefetchedSpace?: Space) => {
    // ✅ MOVED: Command registration now happens globally in App.tsx during bootstrap
    // Commands must be available BEFORE any space initialization to support workflows
    // We keep the contextStore subscription initialization here since it's space-specific
    if (!get().initializing) {
      set({ initializing: true });
      
      try {
        // Initialize context store subscription after stores are ready
        const { initializeContextStoreSubscription } = await import('../components/Space/SpaceChat/store/contextStore');
        initializeContextStoreSubscription();
        log.info('✅ [SpaceStore] Context store subscription initialized');
      } catch (error) {
        log.error('❌ [SpaceStore] Failed to initialize context store subscription:', error);
        set({ initializing: false });
        throw error;
      }
      
      // ✅ NOTE: EntityStore initialization is now handled upfront in SpacePageWrapper
      // to avoid race conditions during rehydration. See SpacePageWrapper.tsx line ~170
    } else {
      log.info('⏭️ [SpaceStore] Context store subscription already initialized, skipping');
    }

    const state = get();
    log.info('🔵 Starting initialization:', {
      requestedSpaceId: spaceId,
      currentSpaceId: state.currentSpaceId,
      initStatus: state.initializationStatus,
      hasActiveSpace: !!state.activeSpace,
      activeSpaceId: state.activeSpace?.id
    });

    // We also maintain this variable separately from state to avoid race conditions
    if (window.__SPACE_INITIALIZING__ === true) {
      log.info('Already initializing (global lock)');
      
      // Return a promise that resolves when the current initialization is done
      return new Promise<void>((resolve) => {
        let attempts = 0;
        const checkDone = () => {
          attempts++;
          if (window.__SPACE_INITIALIZING__ !== true) {
            log.info('Global lock released, continuing');
            resolve();
          } else if (attempts > 50) { // 5 seconds timeout
            log.info('Timeout waiting for lock, forcing release');
            window.__SPACE_INITIALIZING__ = false;
            resolve();
          } else {
            setTimeout(checkDone, 100);
          }
        };
        setTimeout(checkDone, 100);
      });
    }

    if (state.initializationStatus === 'initializing') {
      log.log('[SpaceStore] Already initializing');
      return;
    }

    // Detect a space switch by checking spaceCRDTService.initializedSpaces instead of
    // state.currentSpaceId. The problem: setPageContext() sets state.currentSpaceId to
    // the NEW spaceId BEFORE initializeSpace() runs, so the old check
    // (state.currentSpaceId !== spaceId) was always false — the cleanup block was
    // never entered and the old space's CRDT observer was never disconnected.
    // spaceCRDTService.initializedSpaces is only modified by disconnectSpace/initializeSpace,
    // so it correctly reflects which space has an active CRDT connection.
    const prevCrdtSpaceId = Array.from(spaceCRDTService.initializedSpaces).find(id => id !== spaceId);
    if (prevCrdtSpaceId) {
      const isSpaceSwitch = true;
      const oldSpaceId = prevCrdtSpaceId;
      
      log.log(`[SpaceStore] 🔄 Resetting space store:`, {
        reason: `switching from ${oldSpaceId} to ${spaceId}`,
        detectedVia: 'spaceCRDTService.initializedSpaces',
        currentSpaceId: state.currentSpaceId,
        requestedSpaceId: spaceId,
        initStatus: state.initializationStatus
      });
      
      // CRITICAL: Clean up old space's CRDT connections before resetting
      const { cleanupFn } = state;
      if (cleanupFn) {
        log.info('[SpaceStore] 🧹 Calling cleanup function for old space');
        cleanupFn();
      }
      
      // CRITICAL: If switching spaces, clean up the old space's CRDT observers
      if (isSpaceSwitch && oldSpaceId) {
        log.info(`[SpaceStore] 🧹 Disconnecting CRDT for old space: ${oldSpaceId}`);
        try {
          // Disconnect the specific space instead of disconnectAll (which would affect all spaces)
          spaceCRDTService.disconnectSpace(oldSpaceId, 'space-switch');
        } catch (error) {
          log.error('[SpaceStore] Error disconnecting old space CRDT:', error);
        }
      }
      
      // Reset state but keep the basic structure
      set({ 
        status: 'idle',
        activeSpace: null,
        windows: {},
        contextEntities: {},
        selectedIds: [],
        activeWindowId: null,
        cleanupFn: () => {},
        initializationStatus: 'idle',
        currentSpaceId: undefined,
      });
    }

    try {
      window.__SPACE_INITIALIZING__ = true;
      set({ initializationStatus: 'initializing' });
      
      // Get the space from the backend. SpacePageWrapper has already fetched
      // this exact space via spaceManagementService.initializeSpace, so when it
      // passes that result through we skip a redundant duplicate GET /spaces/:id
      // on the space-load critical path. Shallow-clone the passed-in space so
      // this store owns its top-level object — initialize() may reassign
      // `existingSpace.windows` during ghost-window cleanup, and we must not
      // mutate the instance still held by spaceManagementService.
      const existingSpace =
        prefetchedSpace && prefetchedSpace.id === spaceId
          ? { ...prefetchedSpace }
          : await spaceService.getSpace(spaceId);
      // No need to delete created/updated as UISpace now uses createdAt/updatedAt
      if (existingSpace) {
        log.log('[SpaceStore] 📦 Fetched space from backend');
        log.log('[SpaceStore] 📦 FULL SPACE ENTITY:', JSON.stringify(existingSpace, null, 2));
        log.log('[SpaceStore] 📦 Space summary:', {
          id: existingSpace.id,
          name: existingSpace.name,
          org_id: existingSpace.org_id,
          windowCount: Object.keys(existingSpace.windows || {}).length,
          hasSettings: !!existingSpace.settings,
          hasContextEntities: !!existingSpace.contextEntities,
          createdAt: existingSpace.createdAt,
          updatedAt: existingSpace.updatedAt
        });
      } else {
        log.log('[SpaceStore] No space found in backend');
      }
      
      // Clean up any ghost windows from the backend space
      if (existingSpace && existingSpace.windows) {
        let windowsNeedCleanup = false;
        const validWindows: Record<string, AnyWindowEntity> = {};
        
        // Check each window to see if it's valid
        Object.entries(existingSpace.windows).forEach(([windowId, window]: [string, AnyWindowEntity]) => {
          // Valid windows must have id, type, position, and size
          // Exception: group windows (appType: 'group') are always valid even if they have minimal properties
          if (window && 
              window.id && 
              window.type === 'window' && 
              (window.appType === 'group' || (window.position && window.size))) {
            validWindows[windowId] = window;
          } else {
            // This is a ghost window
            windowsNeedCleanup = true;
            log.log(`[SpaceStore] Found ghost window during init: ${windowId}`, window);
          }
        });
        
        // If we found ghost windows, replace with cleaned version
        if (windowsNeedCleanup) {
          log.log(`[SpaceStore] Cleaning up ${Object.keys(existingSpace.windows).length - Object.keys(validWindows).length} ghost windows`);
          existingSpace.windows = validWindows;
          
          // Also persist the cleaned windows to the backend
          try {
            await spaceService.updateSpace(spaceId, validWindows, existingSpace.settings || {}, existingSpace.activeWindowId, get().contextEntities, get().contextExcerpts);
            log.log('[SpaceStore] Successfully persisted cleaned windows to backend');
          } catch (error) {
            log.error('[SpaceStore] Error persisting cleaned windows:', error);
          }
        }
      }
      
      const newSpace = createUISpace(spaceId);
      
      // Use backend data or create new space - let CRDT service handle merging
      const space = existingSpace || newSpace;
      
      // CRDT service handles merging with existing CRDT data internally
      // Only initialize if not already initialized (prevents duplicate observers in Strict Mode)
      try {
        await spaceCRDTService.initializeSpace(spaceId, space);
      } catch (error) {
        // If initialization was skipped (already initialized), that's fine
        log.log('[SpaceStore] CRDT initialization result:', error);
      }
      
      // ✅ REFACTOR: Normalize loaded space windows (no entity extraction needed)
      log.info('[SpaceStore] 🔄 Normalizing loaded space windows');
      const normalizedWindows: Record<string, NormalizedWindowEntity> = {};
      
      if (space.windows) {
        Object.entries(space.windows).forEach(([windowId, windowEntity]: [string, AnyWindowEntity]) => {
          // Normalize window: entityId is the reference, entity data lives in the entity registry.
          // Recover entityId from any legacy persisted entity field if needed.
          const legacyEntity = (windowEntity as any).entity;
          const normalizedWindow: NormalizedWindowEntity = {
            ...windowEntity,
            entityId: windowEntity.entityId || legacyEntity?._id || legacyEntity?.id,
            entityLlmId: windowEntity.entityLlmId || legacyEntity?.llmId,
          };
          // Remove any legacy embedded entity field from persisted CRDT state
          delete (normalizedWindow as any).entity;
          
          normalizedWindows[windowId] = normalizedWindow;
        });
        
        log.info('[SpaceStore] 🎯 Space normalization complete:', {
          totalWindows: Object.keys(normalizedWindows).length
        });
        
        // Update space with normalized windows
        space.windows = normalizedWindows;
        
        // ✅ REFACTOR: Removed auto-add document to context event dispatch
        // The contextStore subscription to activeWindowId will handle this automatically
      }

      // ✅ CRITICAL FIX: Handle windows that don't have embedded entities but have applicationState
      // This fixes the issue where windows lose entity references during space initialization
      const { getWindowEntityId } = await import('../components/Space/utils/windowUtils');
      const windowsNeedingEntityAssociation = Object.keys(space.windows || {}).filter(windowId => {
        const window = space.windows![windowId] as NormalizedWindowEntity;
        return !window.entityLlmId && getWindowEntityId(window);
      });
      
      if (windowsNeedingEntityAssociation.length > 0) {
        log.info('[SpaceStore] 🔧 Found windows needing entity association:', {
          count: windowsNeedingEntityAssociation.length,
          windowIds: windowsNeedingEntityAssociation
        });
      }
      
      // ✅ PROPER FIX: Subscribe to entity-registry changes to keep window
      // entities fresh as their underlying entity records mutate.
      const { entityRegistry } = await import('./entityRegistry');
      const unsubscribeEntityStore = entityRegistry.subscribeToChanges(() => {
        const currentWindows = get().windows;
        let anyWindowUpdated = false;

        Object.entries(currentWindows).forEach(([windowId, window]) => {
          // log.log(JSON.stringify({
          //   event: '[TITLE_DEBUG] subscribeToChanges:check',
          //   windowId,
          //   appType: window.appType,
          //   entityId: window.entityId ?? null,
          //   windowTitle: window.title ?? null,
          // }));
          if (window.entityId) {
            const freshEntity = entityRegistry.getById(window.entityId) as any;

            log.log(JSON.stringify({
              event: '[TITLE_DEBUG] subscribeToChanges:entity',
              windowId,
              entityId: window.entityId,
              freshEntityName: freshEntity?.name ?? null,
              windowTitle: window.title ?? null,
              willUpdate: !!freshEntity && window.title !== freshEntity.name,
            }));

            if (freshEntity) {
              // ✅ FIX: Compare window.title vs entity.name (not currentEntity.name vs freshEntity.name)
              // This catches cases where the entity was updated but the window title wasn't synced
              if (window.title !== freshEntity.name) {
                // Sync window title when entity name changes (entity data lives in the entity registry)
                get()._applyLocalWindowUpdate(windowId, {
                  title: freshEntity.name,
                }, {
                  source: 'local',
                  syncCRDT: true,
                  log: false
                });

                anyWindowUpdated = true;

                log.info('[SpaceStore] ✅ Synced window entity from entity registry:', {
                  windowId,
                  entityId: freshEntity._id,
                  newName: freshEntity.name
                });
              }
            }
          }
        });
        
        if (anyWindowUpdated) {
          log.info('[SpaceStore] Window entities synced with fresh data from entity registry');
        }
      });

      // The legacy `spaceCRDTService.observeSpace(...)` debounced observer
      // used to live here. It captured `spaceId` in a closure but read live
      // `windows` from `useSpaceStore.getState()` at fire time, and the 500ms
      // debounce timer was not cleared on cleanup — so any update in flight
      // when the user switched spaces would write the new space's windows
      // into the old space's row. It also did a redundant retrying REST PUT
      // of `windows` to the shared backend on every CRDT update, which leaked
      // tabs across users via the shared `entities.skeleton.windows` field.
      //
      // The per-user CRDT sync path is now owned entirely by
      // `setupSpaceObserver` inside `spaceCRDTService.initializeSpace` (set
      // up above via `await spaceCRDTService.initializeSpace(spaceId, space)`),
      // which is attached directly to the per-user Y.Doc with synchronous
      // observer detach on disconnect. Windows are per-user CRDT only and the
      // WS server's genericPersistence is the durable store; no REST write
      // is required.

      // ✅ ENHANCED: Load activeWindowId with priority: localStorage > backend > first window
      // Use the CRDT windows (set by initializeSpace) as the source of truth —
      // space.windows is the REST legacy field and may include stale closed windows.
      const crdtWindowsForActiveId = get().windows;
      const localStorageWindowId = localStorage.getItem(`activeWindowId_${spaceId}`);
      const backendWindowId = space.activeWindowId;
      const firstWindowId = Object.keys(crdtWindowsForActiveId).length > 0
        ? Object.keys(crdtWindowsForActiveId)[0]
        : Object.keys(space.windows || {})[0];
      
      log.log('[SpaceStore] Determining activeWindowId on init:', {
        localStorageWindowId,
        backendWindowId,
        firstWindowId,
        availableWindows: Object.keys(crdtWindowsForActiveId).length
      });
      
      // Priority: localStorage (most recent) > backend (persisted) > first window (fallback)
      let activeWindowId = localStorageWindowId || backendWindowId || firstWindowId || null;
      
      // Validate that the selected window still exists (check CRDT windows first)
      const windowsForValidation = Object.keys(crdtWindowsForActiveId).length > 0
        ? crdtWindowsForActiveId
        : (space.windows || {});
      if (activeWindowId && !windowsForValidation[activeWindowId]) {
        log.log('[SpaceStore] Selected activeWindowId no longer exists, trying fallbacks:', {
          attempted: activeWindowId,
          tryingBackend: !!backendWindowId,
          tryingFirst: !!firstWindowId
        });
        
        // Try backend window if localStorage failed
        if (backendWindowId && windowsForValidation[backendWindowId]) {
          activeWindowId = backendWindowId;
        } else if (firstWindowId) {
          activeWindowId = firstWindowId;
        } else {
          activeWindowId = null;
        }
      }
      
      // After initializeSpace, the CRDT may have populated activeSpace.settings with
      // values that are more current than the backend REST response. This happens when
      // a backend save was deduplicated (another update was in-flight) — the CRDT is
      // always written synchronously on every change, so it is the authoritative
      // source for per-user UI settings like isChatVisible.
      const crdtSettings = get().activeSpace?.settings;

      // Set the space data with normalized entities
      log.info('[SpaceStore] 🎯 Setting initialized space state:', {
        spaceId,
        spaceName: space.name,
        windowCount: Object.keys(space.windows || {}).length,
        activeWindowId: activeWindowId,
        localStorageWindowId,
        backendWindowId,
        firstWindowId,
        source: localStorageWindowId ? 'localStorage' : backendWindowId ? 'backend' : 'firstWindow'
      });

      // For per-user UI settings, prefer the CRDT value (always current) over the
      // backend REST value (can be stale if a save was deduplicated or dropped).
      // Fall back to backend/default when the CRDT has no value for the key.
      const effectiveIsChatVisible =
        crdtSettings?.isChatVisible !== undefined
          ? crdtSettings.isChatVisible
          : (space.settings?.isChatVisible ?? true);

      const effectiveDesktopSidebarOpen =
        crdtSettings?.desktop?.sidebarOpen !== undefined
          ? crdtSettings.desktop.sidebarOpen
          : (space.settings?.desktop?.sidebarOpen ?? true);

      const effectiveDesktopShowRoot =
        crdtSettings?.desktop?.showRoot !== undefined
          ? crdtSettings.desktop.showRoot
          : (space.settings?.desktop?.showRoot ?? false);

      // Merge CRDT settings on top of backend settings so shared fields (viewMode,
      // background, etc.) from the backend are preserved alongside the per-user
      // overrides from CRDT.
      const mergedSettings =
        crdtSettings && Object.keys(crdtSettings).length > 0
          ? { ...space.settings, ...crdtSettings }
          : space.settings;
      
      set({ 
        initializing: false,
        activeSpace: {
          ...space,
          settings: mergedSettings,
        },
        // Prefer windows that CRDT loaded from IDB over the REST skeleton.windows
        // field. The REST field is legacy (per-user windows have been owned by
        // CRDT since the migration) and may contain stale pre-migration data.
        // We fall back to REST only when CRDT is genuinely empty — i.e. the
        // very first time a user opens this space on a device with no prior
        // IDB state. initializeSpace awaits IDB sync before returning, so
        // get().windows already reflects the full local CRDT state here.
        windows: (() => {
          const crdtWindows = get().windows;
          const raw = Object.keys(crdtWindows).length > 0
            ? crdtWindows
            : (space.windows || {});

          // For windows that already have a saved layouts[currentFamily] slice
          // (e.g. the space's viewMode was last switched on another device/tab,
          // or changed via settings since this window's top-level fields were
          // last written), project that slice onto the top-level position/size/
          // zIndex fields — mirrors applyLayoutForViewMode's restore branch so
          // rendering isn't stuck on a stale foreign-family arrangement until
          // the user happens to toggle view modes.
          //
          // For windows that don't yet have a family slice (e.g. pre-migration
          // windows, newly seeded spaces), backfill layouts[currentFamily] from
          // the current top-level fields. This preserves existing Canvas/Spatial
          // arrangements on first load and means the other family generates a
          // fresh cascade on first switch.
          const currentFamily = layoutFamilyOf(mergedSettings?.viewMode) ?? 'spatialCanvas';
          const now = Date.now();
          const backfilled: typeof raw = {};
          let backfilledCount = 0;
          let projectedCount = 0;
          for (const [wid, win] of Object.entries(raw)) {
            const savedSlice = (win as any).layouts?.[currentFamily];
            if (savedSlice) {
              const positionChanged = JSON.stringify(savedSlice.position) !== JSON.stringify((win as any).position);
              const sizeChanged = JSON.stringify(savedSlice.size) !== JSON.stringify((win as any).size);
              const zIndexChanged = savedSlice.zIndex !== (win as any).zIndex;
              if (positionChanged || sizeChanged || zIndexChanged) {
                backfilled[wid] = {
                  ...(win as any),
                  position: savedSlice.position ?? (win as any).position,
                  size: savedSlice.size ?? (win as any).size,
                  zIndex: savedSlice.zIndex ?? (win as any).zIndex,
                };
                projectedCount++;
              } else {
                backfilled[wid] = win;
              }
            } else {
              backfilled[wid] = {
                ...(win as any),
                layouts: {
                  ...(win as any).layouts,
                  [currentFamily]: {
                    position: (win as any).position,
                    size: (win as any).size,
                    zIndex: (win as any).zIndex,
                  },
                },
                updatedAt: now,
              };
              backfilledCount++;
            }
          }
          if (backfilledCount > 0) {
            log.log(`[SpaceStore] Backfilled layouts.${currentFamily} for ${backfilledCount} windows`);
          }
          if (projectedCount > 0) {
            log.log(`[SpaceStore] Projected saved layouts.${currentFamily} onto top-level geometry for ${projectedCount} windows`);
          }
          return backfilled;
        })(),
        // ✅ REFACTOR: entities removed - use entityStore as single source of truth
        contextEntities: space.contextEntities || {}, // ✅ RESTORE: Load persisted context from backend
        contextExcerpts: space.contextExcerpts || [], // ✅ RESTORE: Load persisted excerpts from backend
        initializationStatus: 'initialized',
        currentSpaceId: spaceId,
        cleanupFn: () => {
          // The per-user space CRDT observer is owned by spaceCRDTService
          // and torn down by `disconnectSpace` (which is called from the
          // space-switch path above and on logout). All we own here is the
          // entity registry subscription; tear that down on space-switch / logout.
          unsubscribeEntityStore();
        },
        activeWindowId: activeWindowId,
        isChatVisible: effectiveIsChatVisible,
        // ✨ INSTANT NAVIGATION: Use backend value directly (no default override)
        // Backend sends isSidebarVisible at top level for pending spaces
        isSidebarVisible: space.settings?.sidebar?.isVisible ?? (space.settings as any)?.isSidebarVisible ?? false,
        sidebarWidth: space.settings?.sidebar?.width || 300,
        sidebarMode: space.settings?.sidebar?.mode || 'normal',
        desktopSidebarOpen: effectiveDesktopSidebarOpen,
        desktopShowRoot: effectiveDesktopShowRoot,
      });
      
      // ── Post-CRDT-load window-title reconciliation ──────────────────────────
      // The space layout CRDT may have stale window.title values (e.g. the user
      // renamed a sheet then refreshed before the IDB write completed). Entity
      // stores (orgYDocStore, spaceEntityStore IDB) load faster and have the
      // correct names. Sync any mismatches immediately so there is no visible
      // flash of the old/canonical title.
      //
      // We defer one tick so the setState above settles and `get().windows`
      // reflects the loaded windows before we read them.
      queueMicrotask(() => {
        const loadedWindows = get().windows;
        let reconciled = 0;
        Object.entries(loadedWindows).forEach(([wid, win]) => {
          if (!win.entityId) return;
          const entity = entityRegistry.getById(win.entityId) as any;
          if (!entity?.name || win.title === entity.name) return;
          log.log(JSON.stringify({
            event: '[TITLE_DEBUG] postLoad:titleReconcile',
            windowId: wid,
            appType: win.appType,
            entityId: win.entityId,
            oldTitle: win.title ?? null,
            newTitle: entity.name,
          }));
          get()._applyLocalWindowUpdate(wid, { title: entity.name }, {
            source: 'local',
            syncCRDT: true,
            log: false,
          });
          reconciled++;
        });
        if (reconciled > 0) {
          log.info(`[SpaceStore] Post-load title reconciliation fixed ${reconciled} window(s)`);
        }
      });

      // ✅ DEBUG: Log what we loaded
      log.log('[SpaceStore] 🔍 LOADED SETTINGS FROM BACKEND:', {
        isChatVisible_backend: space.settings?.isChatVisible,
        isChatVisible_crdt: crdtSettings?.isChatVisible,
        isChatVisible_effective: effectiveIsChatVisible,
        isSidebarVisible_topLevel: (space.settings as any)?.isSidebarVisible,
        isSidebarVisible_nested: space.settings?.sidebar?.isVisible,
        viewMode: space.settings?.viewMode,
        isHomeSpace: (space.settings as any)?._isHomeSpace,
        willUseForSidebar: space.settings?.sidebar?.isVisible ?? (space.settings as any)?.isSidebarVisible ?? false,
        fullSettings: space.settings
      });
      
      // ✅ REFACTOR: No entity-registry subscription needed
      // Components access entities directly from the registry via helper methods
      // This eliminates the need for manual sync and prevents staleness bugs

      // 🔔 Check Space lifecycle hooks (triggers triggered Skills on first open, etc.)
      try {
        import('../services/spaceLifecycleService').then(({ spaceLifecycleService }) => {
          spaceLifecycleService.checkAndFireHooks(space);
        });
      } catch { /* non-fatal */ }
      
      // 🔍 REHYDRATION DEBUG: Log detailed state to diagnose initialization issues
      const activeWindowEntityLlmId = activeWindowId && space.windows?.[activeWindowId]?.entityLlmId;
      
      log.info('[SpaceStore] 🔍 REHYDRATION DEBUG:', {
        spaceId,
        activeWindowId,
        activeWindowEntityLlmId,
        windowCount: Object.keys(space.windows || {}).length,
        timestamp: new Date().toISOString()
      });
      
      // Verify activeSpace was set correctly
      const finalState = get();
      log.info('[SpaceStore] ✅ Space initialization completed:', {
        spaceId,
        spaceName: space.name,
        activeWindowId: activeWindowId,
        windowCount: Object.keys(space.windows || {}).length,
        contextEntitiesCount: Object.keys(space.contextEntities || {}).length,
        activeSpaceSet: !!finalState.activeSpace,
        activeSpaceId: finalState.activeSpace?.id,
        activeSpaceName: finalState.activeSpace?.name,
        activeSpaceOrgId: finalState.activeSpace?.org_id,
        initializationStatus: finalState.initializationStatus
      });
      
      // 🎯 CLEAN DATASPACE SUMMARY
      const dataspaceType = space.org_id ? 'ORG' : 'PERSONAL';
      const dataspaceIdentifier = space.org_id || 'personal';
      log.log(`
╔═══════════════════════════════════════════════════════════╗
║  🎯 SPACE LOADED: ${space.name.padEnd(40)}║
╠═══════════════════════════════════════════════════════════╣
║  Space ID:   ${space.id}                         ║
║  Dataspace:  ${dataspaceType.padEnd(8)} (${dataspaceIdentifier.substring(0, 24)})   ║
║  Windows:    ${Object.keys(space.windows || {}).length} open                                          ║
║  Status:     ✅ READY                                      ║
╚═══════════════════════════════════════════════════════════╝
      `);

      // ✅ CRITICAL: Trigger automatic context population after initialization
      // This ensures the active window is added to context on page reload
      // Removed setTimeout - do this immediately since stores are already initialized
      try {
        log.info('[SpaceStore] 🔄 Triggering automatic context population after initialization');
        
        // Import contextStore dynamically to avoid circular dependencies
        const { useContextStore } = await import('../components/Space/SpaceChat/store/contextStore');
        const contextStore = useContextStore.getState();
        
        // Trigger the automatic context logic by simulating a space store change
        // This will populate context with the active window if one exists
        const currentState = get();
        if (currentState.activeWindowId && currentState.windows[currentState.activeWindowId]) {
          log.info('[SpaceStore] 🎯 Active window detected on reload - triggering context population');
          
          // Force a state change to trigger the context subscription
          set((state) => ({ ...state, _refreshTimestamp: Date.now() }));
        }
        
        log.info('[SpaceStore] ✅ Automatic context population completed');
      } catch (error) {
        log.error('[SpaceStore] ❌ Failed to trigger automatic context population:', error);
      }

      // ✅ CRITICAL FIX: Fix entity associations for windows that don't have entityLlmId
      // This must happen after the entity registry is initialized and store is set up
      if (windowsNeedingEntityAssociation.length > 0) {
        // Removed setTimeout - do this immediately since the entity registry is already initialized
        try {
          log.info('[SpaceStore] 🔧 Fixing entity associations for windows after initialization');
          await get().fixAllWindowEntityAssociations();
          log.info('[SpaceStore] ✅ Completed fixing entity associations');
        } catch (error) {
          log.error('[SpaceStore] Failed to fix entity associations:', error);
        }
      }
      
      // ✅ NEW: Automatically clean up orphaned child windows after initialization
      // This prevents phantom windows from persisting across sessions
      try {
        log.info('[SpaceStore] 🧹 Running automatic orphaned window cleanup');
        await get().cleanupStaleWindows();
        log.info('[SpaceStore] ✅ Automatic cleanup completed');
      } catch (error) {
        log.error('[SpaceStore] Failed to clean up orphaned windows:', error);
        // Don't throw - this is a non-critical cleanup operation
      }
    } catch (err) {
      set({ 
        initializationStatus: 'error',
        initializing: false,
      });
      throw err;
    } finally {
      // Release the global lock
      window.__SPACE_INITIALIZING__ = false;
    }
  },

  addWindow: (window: WindowEntity) => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;
    log.log('[SpaceStore][update path] Adding window:', window);
    
    // ✅ SURGICAL FIX: Create normalized window (backward compatible)
    const normalizedWindow: NormalizedWindowEntity = {
      ...window,
      entityId: (window as any).entityId,
      entityLlmId: (window as any).entityLlmId
      // entity field removed from type - use spaceStore.getWindowEntity(windowId) instead
    };
    
    // ✅ DEBUG: Log what entity references we extracted
    if (normalizedWindow.entityId || normalizedWindow.entityLlmId) {
      log.log('[SpaceStore] addWindow - extracted entity references:', {
        windowId: window.id,
        entityId: normalizedWindow.entityId,
        entityLlmId: normalizedWindow.entityLlmId,
        directEntityId: (window as any).entityId,
        directEntityLlmId: (window as any).entityLlmId
      });
    }

    log.log('Adding normalized window to store:', normalizedWindow);
    
    // ✅ NEW: Use helper instead of direct state mutation
    // Ephemeral windows are not synced to CRDT — they live in memory only
    get()._applyLocalWindowUpdate(window.id, normalizedWindow, {
      source: 'local',
      syncCRDT: !normalizedWindow.ephemeral,
      log: true
    });

    // Only set as active window if this is not during space initialization
    // and there's no currently active window AND this isn't from a CRDT update
    const isFromCRDTUpdate = (window as any).__fromCRDTUpdate;
    const shouldSetActive = get().initializationStatus === 'initialized' && 
                           !get().activeWindowId && 
                           !isFromCRDTUpdate;

    if (shouldSetActive) {
      set({ activeWindowId: window.id });
    }
  },

  removeWindow: (windowId: string) => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;
    
    const currentState = get();
    log.log('[SpaceStore][update path] Removing window:', {
      windowId,
      wasActiveWindow: currentState.activeWindowId === windowId,
      currentActiveWindow: currentState.activeWindowId,
      totalWindows: Object.keys(currentState.windows).length,
      windowIds: Object.keys(currentState.windows)
    });

    // ⭐⭐⭐ AUTO-DELETE UNUSED DOCUMENTS (NEW CODE) ⭐⭐⭐
    const windowToDelete = currentState.windows[windowId];
    
    // Get entity from applicationState first (more reliable than window.entity)
    const docState = windowToDelete?.applicationState?.docs as any;
    const entityId = (windowToDelete?.applicationState?.docs as any)?.activeDocId || windowToDelete?.entityId;
    
    log.info('[SpaceStore][AUTO-DELETE] Checking if document should be deleted:', {
      windowId,
      exists: !!windowToDelete,
      hasDocState: !!docState,
      entityId: entityId,
      justCreated: docState?.justCreated
    });

    // Check if this is a docs/document window by looking at applicationState
    if (docState && entityId) {
      log.info('[SpaceStore][AUTO-DELETE] Is a Docs application window', { 
        windowId, 
        entityId,
        appType: windowToDelete?.appType 
      });
      
      log.info('[SpaceStore][AUTO-DELETE] Document state:', {
        windowId,
        entityId,
        justCreated: (docState as any)?.justCreated,
        hasContent: (docState as any)?.hasContent,
        wasEdited: (docState as any)?.wasEdited
      });

      // THE CRITICAL CONDITION: Check if document was never edited AND is auto-generated
      // IMPORTANT: Both conditions must be true:
      // 1. justCreated === true (never edited)
      // 2. autoGenerated === true (created via /new route)
      // If either is false, document is NOT deleted
      const isAutoGenerated = (docState as any)?.autoGenerated === true;
      const isNeverEdited = (docState as any)?.justCreated === true;
      const hasContent = (docState as any)?.hasContent === true;
      
      if (isNeverEdited && isAutoGenerated && !hasContent) {
        log.info('[SpaceStore][AUTO-DELETE] ✅ CONDITION MET: Auto-generated document is untouched', { 
          windowId, 
          docId: entityId,
          isAutoGenerated,
          isNeverEdited,
          hasContent
        });

        if (entityId) {
          log.info('[SpaceStore][AUTO-DELETE] Proceeding with auto-deletion', {
            windowId,
            docId: entityId
          });

          // Check if this document is open in any other windows
          const otherWindows = Object.entries(currentState.windows).filter(
            ([id, win]) => {
              const otherEntityId = (win?.applicationState?.docs as any)?.activeDocId || win?.entityId;
              return id !== windowId && otherEntityId === entityId;
            }
          );

          if (otherWindows.length > 0) {
            log.warn('[SpaceStore][AUTO-DELETE] ⚠️ Document is open in other windows, NOT deleting:', {
              docId: entityId,
              otherWindowCount: otherWindows.length,
              otherWindowIds: otherWindows.map(([id]) => id)
            });
          } else {
            log.info('[SpaceStore][AUTO-DELETE] No other windows have this document open, proceeding with deletion', {
              docId: entityId
            });

            try {
              // SAFETY CHECK: Re-verify ALL three conditions before deletion
              // This prevents accidental deletion if any flag was changed between checks
              const currentWindow = get().windows[windowId];
              const currentDocState = currentWindow?.applicationState?.docs as any;
              
              const finalJustCreated = currentDocState?.justCreated === true;
              const finalAutoGenerated = currentDocState?.autoGenerated === true;
              const finalHasContent = currentDocState?.hasContent === true;
              
              log.info('[SpaceStore][AUTO-DELETE] Running final safety checks before deletion:', {
                docId: entityId,
                windowId,
                finalJustCreated,
                finalAutoGenerated,
                finalHasContent
              });
              
              // CRITICAL: All three must pass, or abort deletion
              if (!finalJustCreated || !finalAutoGenerated || finalHasContent) {
                log.warn('[SpaceStore][AUTO-DELETE] ⚠️ SAFETY CHECK FAILED - NOT DELETING:', {
                  docId: entityId,
                  windowId,
                  reason: 'Document was edited, user-created, or has content',
                  justCreated: finalJustCreated,
                  autoGenerated: finalAutoGenerated,
                  hasContent: finalHasContent
                });
                return;
              }
              
              // ✅ USE ENTITY ACTIONS: Handles deletion, CRDT sync, events, undo/redo
              log.info('[SpaceStore][AUTO-DELETE] Calling actions.softDeleteEntity()...', {
                docId: entityId
              });

              entityActions.softDeleteEntity(entityId).then(async () => {
                log.info('[SpaceStore][AUTO-DELETE] ✅ Successfully auto-deleted document:', {
                  docId: entityId
                });
                
                // ✅ EMIT DELETION EVENT TO NOTIFY SIDEBAR
                try {
                  const { entityEventService } = await import('../services/entityEventService');
                  
                  // Emit event with basic entity info
                  entityEventService.entityDeleted(
                    {
                      _id: entityId,
                      id: entityId,
                      name: 'Deleted Document',
                      entityType: 'Doc',
                    } as any,
                    spaceId,
                    {
                      command: 'auto-delete-untouched-document'
                    }
                  );
                  
                  log.info('[SpaceStore][AUTO-DELETE] ✅ Deletion event emitted to sidebar:', {
                    docId: entityId,
                    spaceId: spaceId
                  });
                } catch (eventError) {
                  log.error('[SpaceStore][AUTO-DELETE] ⚠️ Failed to emit deletion event (but document was deleted):', {
                    docId: entityId,
                    error: eventError instanceof Error ? eventError.message : String(eventError)
                  });
                }
              }).catch((error: any) => {
                log.error('[SpaceStore][AUTO-DELETE] ❌ Failed to auto-delete document:', {
                  docId: entityId,
                  error: error?.message || error
                });
              });
            } catch (deleteError) {
              log.error('[SpaceStore][AUTO-DELETE] ❌ Error during deletion attempt:', {
                docId: entityId,
                error: deleteError instanceof Error ? deleteError.message : String(deleteError)
              });
            }
          }
        } else {
          log.warn('[SpaceStore][AUTO-DELETE] ⚠️ No document ID found, cannot delete', { windowId });
        }
      } else {
        log.info('[SpaceStore][AUTO-DELETE] ❌ CONDITION NOT MET: Will NOT delete document', {
          windowId,
          entityId,
          justCreated: (docState as any)?.justCreated,
          autoGenerated: (docState as any)?.autoGenerated,
          hasContent: (docState as any)?.hasContent,
          reason: 'Not all deletion conditions were met - document is protected'
        });
      }
    } else {
      log.info('[SpaceStore][AUTO-DELETE] Not a documents window or missing entityId, skipping auto-delete check', {
        windowId,
        hasDocState: !!docState,
        entityId: entityId,
        appType: windowToDelete?.appType
      });
    }
    // ⭐⭐⭐ END AUTO-DELETE LOGIC ⭐⭐⭐

    // Check if we're in the middle of a drag operation for this window
    const isDragging = (window as any).__windowDragging && 
                      (window as any).__lastDragWindowId === windowId;
    
    if (isDragging) {
      log.log('[SpaceStore] Delaying window removal - drag operation in progress');
      // Defer removal until drag completes
      setTimeout(() => get().removeWindow(windowId), 100);
      return;
    }

    // Clear any pending drag operations for this window
    if ((window as any).__lastDragWindowId === windowId) {
      delete (window as any).__lastDragWindowId;
      delete (window as any).__lastDragPosition;
    }

    // Find and remove child windows
    const currentWindows = get().windows;
    const childWindowIds = Object.keys(currentWindows).filter(id => 
      currentWindows[id].parentWindowId === windowId
    );

    // Determine next window to focus ONLY if the currently active window is being removed
    let nextActiveWindowId: string | null = null;
    const wasActiveWindow = currentState.activeWindowId === windowId;
    
    if (wasActiveWindow) {
      // ✅ NEW: Check if this window stored a previousActiveWindowId (for launchers like NewWindow/OpenDialog)
      const windowToRemove = currentWindows[windowId];
      const previousActiveWindowId = windowToRemove?.applicationState?.previousActiveWindowId;
      
      log.log('[SpaceStore] Checking for previousActiveWindowId:', {
        windowId,
        windowTitle: windowToRemove?.title,
        hasPreviousWindowId: !!previousActiveWindowId,
        previousActiveWindowId,
        previousWindowExists: previousActiveWindowId ? !!currentWindows[previousActiveWindowId] : false,
        applicationState: windowToRemove?.applicationState
      });
      
      if (previousActiveWindowId && currentWindows[previousActiveWindowId]) {
        // Restore to the window that was active before opening this launcher
        nextActiveWindowId = previousActiveWindowId;
        log.log('[SpaceStore] ✅ Restoring focus to previous window:', {
          previousWindowId: previousActiveWindowId,
          previousWindowTitle: currentWindows[previousActiveWindowId].title
        });
      } else {
        // Check if active window has already changed (e.g., by WindowEntityTabs focus logic)
        // If so, preserve the new active window instead of overriding it
        const currentActiveWindowId = get().activeWindowId;
        
        if (currentActiveWindowId && currentActiveWindowId !== windowId && currentWindows[currentActiveWindowId]) {
          // Active window was already changed to a valid window - preserve it
          nextActiveWindowId = currentActiveWindowId;
          log.log('[SpaceStore] Active window already changed, preserving focus on:', {
            nextWindowId: nextActiveWindowId,
            nextWindowTitle: currentWindows[currentActiveWindowId].title
          });
        } else {
          // Active window hasn't changed or changed to invalid window - use our fallback logic
          // Get all root windows (no child windows) sorted by creation/update time (most recent first)
          // Sort by window title alphabetically as a stable fallback — entity timestamps
          // are not available on the window record itself (entity data lives in the entity registry).
          const rootWindows = Object.values(currentWindows)
            .filter(window => window.type === 'window' && !window.isChildWindow && window.id !== windowId && !childWindowIds.includes(window.id))
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
          
          if (rootWindows.length > 0) {
            nextActiveWindowId = rootWindows[0].id;
            log.log('[SpaceStore] Active window being removed - focusing next window:', {
              nextWindowId: nextActiveWindowId,
              nextWindowTitle: rootWindows[0].title,
              availableWindows: rootWindows.map(w => ({ id: w.id, title: w.title }))
            });
          } else {
            log.log('[SpaceStore] Active window being removed - no remaining windows to focus');
          }
        }
      }
    } else {
      log.log('[SpaceStore] Non-active window being removed - preserving current focus on:', currentState.activeWindowId);
    }

    // Delete the window and atomically update activeWindowId in one set() call
    // to avoid firing the contextStore subscription twice.
    get()._applyLocalWindowDelete(windowId, {
      source: 'local',
      syncCRDT: true,
      log: true,
      nextActiveWindowId: wasActiveWindow ? nextActiveWindowId : undefined
    });
    
    childWindowIds.forEach(childId => {
      get()._applyLocalWindowDelete(childId, {
        source: 'local',
        syncCRDT: true,
        log: true
      });
    });
    
    set((state) => ({
      selectedIds: state.selectedIds.filter((id) => id !== windowId && !childWindowIds.includes(id)),
    }));
    
    log.log('[SpaceStore] State update after window removal:', {
      removedWindowId: windowId,
      wasActiveWindow,
      newActiveWindowId: wasActiveWindow ? nextActiveWindowId : currentState.activeWindowId,
      remainingWindows: Object.keys(get().windows).length
    });

    // Cancel any pending debounced operations for this window
    debouncedPersistStore.cancel();

    // Note: CRDT sync is now handled by the helper (_applyLocalWindowDelete)
    
    // Update CRDT with new active window only if the active window was removed and changed
    if (wasActiveWindow) {
      if (nextActiveWindowId) {
        spaceCRDTService.updateActiveWindowId(spaceId, nextActiveWindowId);
        log.log('[SpaceStore] Updated CRDT with new active window:', nextActiveWindowId);
      } else {
        spaceCRDTService.updateActiveWindowId(spaceId, '');
        log.log('[SpaceStore] Cleared active window in CRDT - no remaining windows');
      }
    }
    // If wasActiveWindow is false, we don't update CRDT because the active window hasn't changed
    
    // Ensure immediate backend save to prevent window repopulation
    // Use flush to bypass debouncing and save immediately
    setTimeout(async () => {
      // Double-check the window is still removed before saving
      const currentWindows = get().windows;
      if (currentWindows[windowId]) {
        log.warn(`[SpaceStore] Window ${windowId} reappeared before backend save - skipping save`);
        return;
      }
      
      try {
        log.info(`[SpaceStore] Force saving to backend after removing window ${windowId}`);
        await backendSaveQueue.flush(); // Force immediate save
        log.info(`[SpaceStore] ✅ Backend save completed for window removal`);
      } catch (error) {
        log.error(`[SpaceStore] ❌ Failed to save window removal to backend:`, error);
      }
    }, 300); // Delay to give CRDT more time
  },

  // Synchronous window update — use for high-frequency operations (drag, resize) and
  // applicationState patches that don't change entityId or appType.
  // For navigation that changes entityId/appType, prefer actions.updateWindow (async)
  // which also handles transient launcher entity cleanup.
  updateWindow: (windowId: string, updates: Partial<AnyWindowEntity>) => {
    const isDragOperation = window.__windowDragging && updates.position;

    if (!get().activeSpace?.id) return;

    if (!get().windows[windowId]) {
      log.warn('[SpaceStore] Attempted to update non-existent window:', { windowId, updates: Object.keys(updates) });
      return;
    }

    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: !isDragOperation,
    });

    if (!isDragOperation) {
      backendSaveQueue.schedule(300);
    } else {
      debouncedPersistStore();
    }
  },

  // Thin wrapper so z-order writes flow through _applyLocalWindowUpdate and get
  // mirrored into the active layout-family slice alongside position/size.
  setWindowZIndex: (windowId: string, zIndex: number) => {
    if (!get().windows[windowId]) return;
    const activeFamily = layoutFamilyOf(get().activeSpace?.settings?.viewMode);
    log.log(JSON.stringify({ event: '[Z_INDEX] spaceStore.setWindowZIndex', windowId, zIndex, activeFamily, prevZ: get().windows[windowId]?.zIndex ?? null }));
    get()._applyLocalWindowUpdate(windowId, { zIndex }, {
      source: 'local',
      syncCRDT: true,
      log: false,
    });
  },

  selectWindow: (windowId: string, isMultiSelect: boolean) => {
    set((state) => {
      if (isMultiSelect) {
        // Toggle: if already selected, remove it; otherwise add it
        const isAlreadySelected = state.selectedIds.includes(windowId);
        return {
          selectedIds: isAlreadySelected
            ? state.selectedIds.filter(id => id !== windowId)
            : [...state.selectedIds, windowId]
        };
      } else {
        // Plain click: clear all and select just this one
        return {
          selectedIds: windowId ? [windowId] : []
        };
      }
    });
  },

  groupSelected: () => {
    const { selectedIds } = get();
    log.debug('Store groupSelected called with:', { selectedIds });

    if (selectedIds.length < 2) {
      log.debug('Not enough windows selected to group');
      return;
    }

    const spaceId = get().activeSpace?.id;
    if (spaceId) {
      log.debug('Triggering CRDT groupWindows');
      spaceCRDTService.groupWindows(spaceId, selectedIds);
    }
  },

  ungroup: (windowId) => {
    const window = get().windows[windowId] as WindowEntity;
    if (!window?.tabs) return;

    // Remove tab properties from window
    const updatedWindow = {
      ...window,
      tabs: undefined,
      activeTabId: undefined,
      isParentWindow: undefined
    };

    set(state => ({
      windows: {
        ...state.windows,
        [windowId]: updatedWindow
      }
    }));

    spaceCRDTService.ungroupWindows(windowId);
  },

  setWindows: (windows: AnyWindowEntity[]) => {
    log.debug('Store setWindows called with:', {
      windowCount: windows?.length || 0,
      windows,
    });

    set((state) => {
      const newWindows = (windows || []).reduce((acc, win) => {
        // All windows are now just WindowEntity
        if (win && win.id && !acc[win.id]) {
          acc[win.id] = win;
        }
        return acc;
      }, {} as Record<string, AnyWindowEntity>);

      return { windows: newWindows };
    });
  },

  setStatus: (status) => {
    set({ status });
  },

  /**
   * Canonical rename entry point for ANY space. Pass `targetSpaceId` to rename a
   * specific space; omit it to rename the active space. This is the single funnel
   * that fans the new name out to every surface that displays it: the backend,
   * the active desktop title (activeSpace), the entity registry (Home), and the
   * systemStore cache (sidebar / switcher). Each surface is keyed by spaceId so a
   * lagging one (e.g. activeSpace not yet pointing at a freshly-created desktop)
   * never blocks the others.
   */
  updateSpaceName: async (newName: string, targetSpaceId?: string) => {
    const state = get();
    const activeSpace = state.activeSpace;
    const spaceId = targetSpaceId ?? activeSpace?.id ?? state.currentSpaceId;
    log.log('[deskRename] updateSpaceName called', {
      newName,
      spaceId,
      activeSpaceId: activeSpace?.id,
      currentSpaceId: state.currentSpaceId,
    });

    if (!spaceId) {
      log.warn('[deskRename] updateSpaceName aborted — no target space');
      throw new Error('[SpaceStore] Cannot update name - no target space');
    }

    try {
      await spaceService.updateSpaceName(spaceId, newName);
      log.log('[deskRename] backend PATCH ok', { spaceId, newName });
      const updatedAt = new Date().toISOString();

      // 1. Active desktop title — only when the target IS the active space.
      const isActiveTarget = activeSpace?.id === spaceId || state.currentSpaceId === spaceId;
      log.log('[deskRename] updateSpaceName applying', { spaceId, newName, isActiveTarget, activeSpaceId: activeSpace?.id });
      if (activeSpace && isActiveTarget) {
        set((s) => ({
          activeSpace: s.activeSpace
            ? { ...s.activeSpace, id: s.activeSpace.id ?? spaceId, name: newName, updatedAt }
            : null,
        }));
        log.log('[deskRename] activeSpace.name set', { spaceId, newName });
      } else {
        log.warn('[deskRename] activeSpace NOT updated (target is not the active space)', { spaceId, activeSpaceId: activeSpace?.id, currentSpaceId: state.currentSpaceId });
      }

      // 2. Entity registry (Home reads from here).
      const inRegistry = !!entityRegistry.getById(spaceId);
      log.log('[deskRename] registry update', { spaceId, inRegistry });
      if (inRegistry) {
        entityActions.updateEntity(spaceId, { name: newName, updated_at: updatedAt });
      }

      // 3. systemStore cache (sidebar / switcher cards), keyed by spaceId.
      const useSystemStore = (await import('./systemStore')).default;
      const cachedSpace = useSystemStore.getState().getSpaceById(spaceId);
      log.log('[deskRename] systemStore cache update', { spaceId, inCache: !!cachedSpace });
      if (cachedSpace) {
        useSystemStore.getState()._applyLocalSpaceUpdate(
          { ...cachedSpace, id: cachedSpace.id ?? spaceId, name: newName, updatedAt },
          { source: 'local', log: false },
        );
      }

      log.log('[deskRename] ✅ rename complete across all surfaces', { spaceId, newName });
    } catch (error) {
      log.warn('[deskRename] ❌ updateSpaceName failed', { spaceId, newName, error });
      throw error;
    }
  },

  updateSpaceChatSettings: async (spaceChatUpdates: Partial<NonNullable<SpaceSettings['spaceChat']>>) => {
    // log.log(formatJSON(spaceChatUpdates, '[SpaceStore] Updating SpaceChat settings'));
    const spaceId = get().activeSpace?.id;
    const activeSpace = get().activeSpace;
    if (!spaceId || !activeSpace) {
      log.error('Cannot update SpaceChat settings - no active space');
      return;
    }

    try {
      // Merge with existing spaceChat settings
      const currentSpaceChatSettings = activeSpace.settings.spaceChat || {
        isHistoryOpen: false,
        mode: 'chat',
        model: await modelService.getUserPreferredModel(),
        isContextVisible: false,
        widths: { focused: 450, spatial: 500 }
      };

      const newSpaceChatSettings = { ...currentSpaceChatSettings, ...spaceChatUpdates };

      // Strip per-user navigation fields before writing to shared storage.
      // tabs, activeTabId, and currentThreadIds belong exclusively in the
      // per-user space-chat:{spaceId}:{userId} Y.Doc — never in the shared
      // Space entity in MongoDB or the shared space:{spaceId} Y.Doc.
      const { tabs: _t, activeTabId: _a, currentThreadIds: _c, ...sharedSpaceChatSettings } = newSpaceChatSettings as any;
      const sharedSettings = { ...activeSpace.settings, spaceChat: sharedSpaceChatSettings };

      // Update via spaceService (which handles backend persistence)
      await spaceService.updateSpace(spaceId, activeSpace.windows, sharedSettings, get().activeWindowId, get().contextEntities, get().contextExcerpts);
      
      // Update local state — keep the full merged object locally (never broadcast)
      const newSettings = { ...activeSpace.settings, spaceChat: newSpaceChatSettings };
      set((state) => ({
        activeSpace: state.activeSpace ? {
          ...state.activeSpace,
          settings: newSettings
        } : null
      }));

      // Broadcast shared settings (no per-user fields) to other users via CRDT
      spaceCRDTService.updateSettings(spaceId, sharedSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
    } catch (error) {
      log.error('Failed to update SpaceChat settings:', error);
      throw error;
    }
  },

  updateEntitySidebarSettings: async (sidebarSettings: Partial<NonNullable<SpaceSettings['entitySidebar']>>) => {
    const spaceId = get().activeSpace?.id;
    const activeSpace = get().activeSpace;
    if (!spaceId || !activeSpace) {
      log.error('Cannot update EntitySidebar settings - no active space');
      return;
    }

    try {
      // Merge with existing entitySidebar settings
      const currentSidebarSettings = activeSpace.settings.entitySidebar || {
        expandedFolderIds: [],
        currentFolderId: null,
        folderPath: []
      };

      const newSidebarSettings = { ...currentSidebarSettings, ...sidebarSettings };
      const newSettings = { ...activeSpace.settings, entitySidebar: newSidebarSettings };

      // Update via spaceService (which handles backend persistence)
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, get().activeWindowId, get().contextEntities, get().contextExcerpts);
      
      // Update local state after successful backend update
      set((state) => ({
        activeSpace: state.activeSpace ? {
          ...state.activeSpace,
          settings: newSettings
        } : null
      }));

      // Also update CRDT for real-time collaboration
      spaceCRDTService.updateSettings(spaceId, newSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
    } catch (error) {
      log.error('Failed to update EntitySidebar settings:', error);
      throw error;
    }
  },

  cleanup: () => {
    const { cleanupFn } = get();
    log.log('[SpaceStore] Cleaning up...');
    if (cleanupFn) {
      cleanupFn();
    }
    
    useSpatialUIStore.getState().reset();
    
    set({ 
      status: 'idle',
      activeSpace: null,
      windows: {},
      contextEntities: {},
      initializationStatus: 'idle',
      initializing: false,
      cleanupFn: () => {},
      appFocusMode: null,
      desktopSidebarOpenBeforeFocus: null,
    } as Partial<SpaceStore>);
  },

  setBackground: async (background: SpaceBackground) => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    const activeSpace = get().activeSpace;
    if (!activeSpace) return;

    const newSettings = {
      ...activeSpace.settings,
      background
    };

    // Update local state immediately for UI responsiveness
    set((state) => ({
      activeSpace: state.activeSpace ? {
        ...state.activeSpace,
        settings: newSettings
      } : null
    }));

    try {
      // Persist to backend (include activeWindowId)
      await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, get().activeWindowId, get().contextEntities, get().contextExcerpts);
      log.log('[SpaceStore] Successfully updated background in backend');
      
      // Also update CRDT for real-time collaboration
      spaceCRDTService.updateSettings(spaceId, newSettings);
      
      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...activeSpace,
        settings: newSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
      log.log('[SpaceStore] ✅ Updated systemStore cache with new background');
    } catch (error) {
      log.error('[SpaceStore] Error updating background in backend:', error);
    }
  },

  toggleDebugger: () => set(state => ({ showDebugger: !state.showDebugger })),

  setActiveWindow: (windowId: string | null) => {
    // Clearing (no window left open) is the only case this handles directly —
    // bringWindowToFront requires an existing window to raise, which doesn't
    // apply here. Every other case delegates to bringWindowToFront so a window
    // marked "active" always gets its z-index raised too — calling this instead
    // of bringWindowToFront was a second, independent way the "active but not
    // visually on top" bug could occur (see bringWindowToFront's doc comment).
    if (windowId === null) {
      log.log('[SpaceStore] Clearing active window');
      set({ activeWindowId: null });
      return;
    }
    get().bringWindowToFront(windowId);
  },

  bringWindowToFront: (windowId: string) => {
    const { windows, initializationStatus, activeWindowId: currentActiveWindowId } = get();
    if (!windows[windowId]) {
      log.warn('[SpaceStore] Attempted to bring non-existent window to front:', windowId);
      log.log(JSON.stringify({ event: '[Z_INDEX] spaceStore.bringWindowToFront:no_window', windowId }));
      return;
    }

    // Single entrypoint for focusing a window: always raise its visual
    // stacking order (and reconcile the persisted zIndex mirror) here so
    // callers never need to separately call the spatial store — a split call
    // site that forgot the z-raise half was the root cause of windows
    // reloading in the wrong stacking order. spatialUIStore.bringWindowToFront
    // has its own idempotent "already on top" guard, so this is a cheap no-op
    // when nothing needs to change, independent of the activeWindowId skip below.
    useSpatialUIStore.getState().bringWindowToFront(windowId);

    // ✅ PERFORMANCE FIX: Skip the activeWindowId side-effects if already active
    // (prevents redundant state update / localStorage / backend writes).
    if (currentActiveWindowId === windowId) {
      log.log('[SpaceStore] bringWindowToFront skipped - window already active:', windowId);
      // log.log(JSON.stringify({ event: '[Z_INDEX] spaceStore.bringWindowToFront:skipped_already_active', windowId, currentActiveWindowId }));
      return;
    }

    log.log(JSON.stringify({ event: '[Z_INDEX] spaceStore.bringWindowToFront', windowId, prevActiveWindowId: currentActiveWindowId, windowZIndex: windows[windowId]?.zIndex ?? null }));

    if (initializationStatus === 'initialized' && currentActiveWindowId) {
      log.log('[SpaceStore] 🔄 ActiveWindowId changed:', {
        from: windows[currentActiveWindowId]?.title,
        to: windows[windowId]?.title,
      });
    }

    log.log('[SpaceStore] bringWindowToFront setting activeWindowId to:', windowId);
    
    set({ 
      activeWindowId: windowId
    });
    
    const spaceId = get().activeSpace?.id;
    if (spaceId) {
      // Debounced — localStorage writes are fire-and-forget on the focus path.
      debouncedSaveActiveWindowId(spaceId, windowId);
      
      backendSaveQueue.schedule(500);
      
      (window as any).closeContextMenu?.();
    }
  },

  setViewMode: async (viewMode: 'spatial' | 'focused' | 'gui2' | 'home' | 'canvas' | 'spatialCanvas') => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    const activeSpace = get().activeSpace;
    if (!activeSpace) return;

    // Capture previous mode before the optimistic update so applyLayoutForViewMode
    // can save the old family's geometry snapshot.
    const prevViewMode = get().activeSpace?.settings?.viewMode;

    // Focused mode is a full-screen, single-window surface — the "Open Windows"
    // sidebar has no room to coexist with it. Force it closed on entry and, on
    // exit, bring it back only if it was actually open beforehand.
    if (viewMode === 'focused' && prevViewMode !== 'focused') {
      const wasOpen = get().desktopSidebarOpen;
      set({ desktopSidebarOpenBeforeFocus: wasOpen });
      if (wasOpen) get().setDesktopSidebarOpen(false);
    } else if (viewMode !== 'focused' && prevViewMode === 'focused') {
      const wasOpen = get().desktopSidebarOpenBeforeFocus;
      set({ desktopSidebarOpenBeforeFocus: null });
      if (wasOpen) get().setDesktopSidebarOpen(true);
    }

    // Update local state immediately for UI responsiveness. Re-read the latest
    // settings inside the updater so a concurrent setViewMode call (e.g. a rapid
    // enter→exit focus toggle) can't be clobbered by a stale snapshot.
    set((state) => ({
      activeSpace: state.activeSpace ? {
        ...state.activeSpace,
        settings: { ...state.activeSpace.settings, viewMode }
      } : null
    }));

    // Project window geometry for the new mode (save old family, load or cascade new).
    get().applyLayoutForViewMode(prevViewMode, viewMode);

    // Sync ONLY the changed field to the CRDT, synchronously and BEFORE the
    // async backend round-trip. This guarantees concurrent view-mode changes
    // land in the shared settings map in invocation order (last action wins).
    // Previously the CRDT write happened after `await updateSpace`, so a slow
    // "enter focused" write could resolve after the "exit" write and leave the
    // map holding a stale 'focused' value — which snapped the layout back to
    // focused on the next CRDT sync.
    spaceCRDTService.updateSettings(spaceId, { viewMode });

    try {
      const latestSettings = get().activeSpace?.settings ?? { ...activeSpace.settings, viewMode };
      const latestWindows = get().activeSpace?.windows ?? activeSpace.windows;

      // Persist to backend
      await spaceService.updateSpace(spaceId, latestWindows, latestSettings, get().activeWindowId, get().contextEntities);
      log.log('[SpaceStore] Successfully updated viewMode in backend');

      // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
      const useSystemStore = (await import('./systemStore')).default;
      const updatedSpace = {
        ...(get().activeSpace ?? activeSpace),
        settings: latestSettings,
        updatedAt: new Date().toISOString()
      };
      useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
        source: 'local',
        log: false
      });
      log.log('[SpaceStore] ✅ Updated systemStore cache with new viewMode');
    } catch (error) {
      log.error('[SpaceStore] Error updating viewMode in backend:', error);
    }
  },

  applyLayoutForViewMode: (prevViewMode: string | undefined, nextViewMode: string) => {
    const nextFam = layoutFamilyOf(nextViewMode);
    if (!nextFam) return; // focused / home / gui2 — no spatial geometry needed

    const prevFam = layoutFamilyOf(prevViewMode);
    const spaceId = get().activeSpace?.id;
    const windows = get().windows;

    const now = Date.now();
    const updatedWindows: typeof windows = { ...windows };
    const needsCascade: string[] = [];

    for (const [wid, win] of Object.entries(windows)) {
      // Only reproject real (non-tab) windows
      if (win.type !== 'window' || win.isTab || win.appType === 'group') continue;

      const updated = { ...win, updatedAt: now };

      // Save current top-level into the old family snapshot (defensive)
      if (prevFam) {
        updated.layouts = {
          ...updated.layouts,
          [prevFam]: {
            position: win.position,
            size: win.size,
            zIndex: win.zIndex,
          },
        };
      }

      const savedSlice = updated.layouts?.[nextFam];
      if (savedSlice) {
        // Restore from saved family snapshot
        updated.position = savedSlice.position;
        updated.size = savedSlice.size;
        if (savedSlice.zIndex !== undefined) updated.zIndex = savedSlice.zIndex;
      } else {
        // No saved layout — will cascade below
        needsCascade.push(wid);
      }

      updatedWindows[wid] = updated;
    }

    // Generate fresh on-screen cascade for windows with no saved family slice
    if (needsCascade.length > 0) {
      const cascade = computeCascadeLayout(needsCascade, { family: nextFam, spaceId: spaceId ?? undefined });
      for (const wid of needsCascade) {
        const layout = cascade[wid];
        if (!layout) continue;
        updatedWindows[wid] = {
          ...updatedWindows[wid],
          position: layout.position,
          size: layout.size,
          zIndex: layout.zIndex,
          layouts: {
            ...updatedWindows[wid].layouts,
            [nextFam]: layout,
          },
          updatedAt: now,
        };
      }
    }

    // Commit all window geometry changes in one state update (beats CRDT stale-guard
    // because updatedAt is fresh) and persist per-window to CRDT.
    set({ windows: updatedWindows });
    if (spaceId) {
      const changed = Object.values(updatedWindows).filter(w => w.updatedAt === now);
      for (const w of changed) {
        spaceCRDTService.updateWindow(spaceId, w.id, w);
      }
      backendSaveQueue.flush();
    }

    // Re-hydrate z-order for the new family — SYNCHRONOUSLY. The previous
    // dynamic import deferred this to a microtask, so a focus click landing
    // between `set({ windows })` above and the hydration would raise a window's
    // z only to have it immediately wiped by the deferred normalize. The static
    // import is safe: the spaceStore ⇄ spaceSpatialUIStore cycle only touches
    // each other inside actions (never at module-eval time).
    useSpatialUIStore.getState().hydrateZIndexesFromWindows(updatedWindows as any, nextFam);

    const zSnapshot = Object.fromEntries(Object.entries(updatedWindows).map(([id, w]) => [id, (w as any).zIndex ?? null]));
    log.log(JSON.stringify({ event: '[Z_INDEX] applyLayoutForViewMode', prevFam: prevFam ?? 'none', nextFam, reprojected: Object.keys(updatedWindows).length - needsCascade.length, cascaded: needsCascade.length, windowZIndexAfterReproject: zSnapshot }));
    log.log(`[SpaceStore] applyLayoutForViewMode ${prevFam ?? 'none'}→${nextFam} reprojected=${Object.keys(updatedWindows).length - needsCascade.length} cascaded=${needsCascade.length}`);
  },

  enterFocusedMode: async (windowId: string) => {
    log.log('[SpaceStore] enterFocusedMode', { windowId });
    get().bringWindowToFront(windowId);
    await get().setViewMode('focused');
  },

  exitFocusedMode: async () => {
    // Spatial is the default active-working mode — exit focused mode always
    // returns to Spatial so users land in their familiar working layout.
    log.log('[SpaceStore] exitFocusedMode → spatial');
    await get().setViewMode('spatial');
  },

  saveToBackend: async () => {
    const spaceId = get().activeSpace?.id;
    if (!spaceId) return;

    const state = get();
    // Filter out ephemeral windows — they must not be persisted to the backend
    const windows = Object.fromEntries(
      Object.entries(state.windows).filter(([, w]) => !w.ephemeral)
    );
    const settings = state.activeSpace?.settings;
    const activeWindowId = state.activeWindowId;
    
    log.log('[SpaceStore] Persisting to backend:', {
      spaceId,
      windows: Object.keys(windows).length,
      activeWindowId,
      note: 'Now persisting activeWindowId to backend'
    });
    
    log.log('[SpaceStore] Persisting to backend after space update');
    
    try {
      await spaceService.updateSpace(spaceId, windows, settings || {}, activeWindowId, state.contextEntities, state.contextExcerpts);
      log.log('[SpaceStore] ✅ Successfully persisted space to backend (including activeWindowId)');
    } catch (error) {
      log.error('[SpaceStore] ❌ CRITICAL: Failed to persist space to backend!', {
        error: error instanceof Error ? error.message : String(error),
        spaceId,
        windowCount: Object.keys(windows).length,
        contextEntitiesCount: Object.keys(state.contextEntities || {}).length
      });
      // Re-throw so callers know the save failed
      throw error;
    }
  },

  // Entity access
  refreshWindowEntityFromBackend: async (windowId: string) => {
    const state = get();
    const window = state.windows[windowId];
    if (!window?.entityId) {
      log.warn('[SpaceStore] Cannot refresh window entity - no entityId:', windowId);
      return;
    }
    
    try {
      log.info('[SpaceStore] Refreshing window entity from backend:', { windowId, entityId: window.entityId });
      // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
      const freshEntity = await entityActions.getEntity(window.entityId);
      
      if (freshEntity) {
        log.info('[SpaceStore] ✅ Refreshed entity from backend:', { 
          entityId: freshEntity._id, 
          name: freshEntity.name,
          hasLlmId: !!freshEntity.llmId,
          llmId: freshEntity.llmId 
        });
        
        // ✅ REFACTOR: No need to store in spaceStore - the entity registry is the single source of truth
        // Entity is already in the registry from the getEntity call
      } else {
        log.warn('[SpaceStore] No entity returned from backend:', window.entityId);
      }
    } catch (error) {
      log.error('[SpaceStore] Failed to refresh window entity:', error);
    }
  },
  
  getWorkspaceEntities: () => {
    const state = get();
    const workspaceEntities: Array<{ llmId: string; name: string; type: string; windowId: string; entity: any }> = [];
    
    log.log('[SpaceStore] Getting workspace entities from windows:', {
      totalWindows: Object.keys(state.windows).length,
      windowIds: Object.keys(state.windows)
    });
    
    // ✅ REFACTOR: Use the entity registry as the single source of truth
    for (const [windowId, windowEntity] of Object.entries(state.windows)) {
      const entityLlmId = windowEntity.entityLlmId;
      const entity = entityLlmId ? (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find((e: any) => e.llmId === entityLlmId) : null;
      
      if (entity && entity.llmId) {
        workspaceEntities.push({
          llmId: entity.llmId,
          name: entity.name || windowEntity.title || 'Untitled',
          type: entity.entityType || 'Doc',
          windowId,
          entity
        });
        log.log('[SpaceStore] ✅ Added entity with llmId:', entity.llmId);
      } else if (entityLlmId) {
        log.warn('[SpaceStore] Skipping window with missing entity:', { 
          entityLlmId, 
          windowId
        });
      }
    }
    
    log.log('[SpaceStore] Found workspace entities:', workspaceEntities.length, workspaceEntities.map(e => `${e.name}(${e.llmId})`));
    return workspaceEntities;
  },

  // Sidebar actions
  setSidebarVisible: async (isVisible: boolean) => {
    // Update local state immediately for UI responsiveness
    set({ isSidebarVisible: isVisible });
    
    // Persist to backend
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    
    if (spaceId && activeSpace) {
      const newSidebarSettings = {
        isVisible,
        width: state.sidebarWidth,
        mode: state.sidebarMode,
      };
      
      const newSettings = {
        ...activeSpace.settings,
        sidebar: newSidebarSettings
      };
      
      try {
        // Update backend first (include activeWindowId)
        await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, state.activeWindowId, state.contextEntities, state.contextExcerpts);
        log.log('[SpaceStore] Successfully updated sidebar visibility in backend');
        
        // Update local activeSpace to keep in sync
        set((state) => ({
          activeSpace: state.activeSpace ? {
            ...state.activeSpace,
            settings: newSettings
          } : null
        }));
        
        // Also update CRDT for real-time collaboration
        spaceCRDTService.updateSettings(spaceId, newSettings);
        
        // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
        const useSystemStore = (await import('./systemStore')).default;
        const updatedSpace = {
          ...activeSpace,
          settings: newSettings,
          updatedAt: new Date().toISOString()
        };
        useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
          source: 'local',
          log: false
        });
      } catch (error) {
        log.error('[SpaceStore] Error updating sidebar visibility in backend:', error);
      }
    }
  },

  setSidebarWidth: async (width: number) => {
    // Update local state immediately for UI responsiveness
    // Also mirror the width into activeSpace.settings.sidebar so scheduled saves persist the latest value
    set((state) => {
      if (!state.activeSpace) {
        return { sidebarWidth: width } as Partial<SpaceStore>;
      }
      const newSidebarSettings = {
        isVisible: state.isSidebarVisible,
        width,
        mode: state.sidebarMode,
      };
      const newSettings = {
        ...state.activeSpace.settings,
        sidebar: newSidebarSettings,
      };
      return {
        sidebarWidth: width,
        activeSpace: {
          ...state.activeSpace,
          settings: newSettings,
        },
      } as Partial<SpaceStore>;
    });

    // Debounce backend persistence to avoid hammering the API during drag
    backendSaveQueue.schedule(300);
  },

  setSidebarMode: async (mode: 'normal' | 'overlay') => {
    // Update local state immediately for UI responsiveness
    set({ sidebarMode: mode });
    
    // Persist to backend
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    
    if (spaceId && activeSpace) {
      const newSidebarSettings = {
        isVisible: state.isSidebarVisible,
        width: state.sidebarWidth,
        mode,
      };
      
      const newSettings = {
        ...activeSpace.settings,
        sidebar: newSidebarSettings
      };
      
      try {
        // Update backend first
        await spaceService.updateSpace(spaceId, activeSpace.windows, newSettings, state.activeWindowId, state.contextEntities);
        log.log('[SpaceStore] Successfully updated sidebar mode in backend');
        
        // Update local activeSpace to keep in sync
        set((state) => ({
          activeSpace: state.activeSpace ? {
            ...state.activeSpace,
            settings: newSettings
          } : null
        }));
        
        // Also update CRDT for real-time collaboration
        spaceCRDTService.updateSettings(spaceId, newSettings);
        
        // ✅ PROPER ORCHESTRATION: Update systemStore cache to keep UI in sync
        const useSystemStore = (await import('./systemStore')).default;
        const updatedSpace = {
          ...activeSpace,
          settings: newSettings,
          updatedAt: new Date().toISOString()
        };
        useSystemStore.getState()._applyLocalSpaceUpdate(updatedSpace, {
          source: 'local',
          log: false
        });
      } catch (error) {
        log.error('[SpaceStore] Error updating sidebar mode in backend:', error);
      }
    }
  },

  setDesktopSidebarOpen: async (open: boolean) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    const newSettings = activeSpace
      ? { ...activeSpace.settings, desktop: { ...activeSpace.settings?.desktop, sidebarOpen: open, showRoot: state.desktopShowRoot } }
      : undefined;

    // Update local state + activeSpace.settings + CRDT synchronously; persist
    // via the (non-lossy) save queue. See setIsChatVisible for the rationale.
    set((s) => ({
      desktopSidebarOpen: open,
      activeSpace: s.activeSpace && newSettings ? { ...s.activeSpace, settings: newSettings } : s.activeSpace,
    }));

    if (!spaceId || !activeSpace || !newSettings) return;
    spaceCRDTService.updateSettings(spaceId, newSettings);
    backendSaveQueue.schedule(300);
  },

  setDesktopShowRoot: async (showRoot: boolean) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    const activeSpace = state.activeSpace;
    const newSettings = activeSpace
      ? { ...activeSpace.settings, desktop: { ...activeSpace.settings?.desktop, sidebarOpen: state.desktopSidebarOpen, showRoot } }
      : undefined;

    set((s) => ({
      desktopShowRoot: showRoot,
      activeSpace: s.activeSpace && newSettings ? { ...s.activeSpace, settings: newSettings } : s.activeSpace,
    }));

    if (!spaceId || !activeSpace || !newSettings) return;
    spaceCRDTService.updateSettings(spaceId, newSettings);
    backendSaveQueue.schedule(300);
  },

  toggleChatExpanded: async () => {
    const state = get();
    const newExpandedState = !state.isChatExpanded;

    if (newExpandedState) {
      // EXPANDING: remember sidebar state so collapse can restore it.
      // Chat width is global (localStorage) and does not need round-tripping.
      set({
        previousLayoutState: {
          sidebarVisible: state.isSidebarVisible,
          sidebarWidth: state.sidebarWidth,
        },
        isChatExpanded: true,
        isSidebarVisible: false,
      });
    } else {
      const prev = state.previousLayoutState;
      if (prev) {
        set({
          isChatExpanded: false,
          isSidebarVisible: prev.sidebarVisible,
          sidebarWidth: prev.sidebarWidth,
          previousLayoutState: null,
        });
      } else {
        set({ isChatExpanded: false });
      }
    }

    // Persist to backend (sidebar state, etc.)
    backendSaveQueue.schedule(300);
  },

  // Hierarchical operations
  moveWindowToGroup: async (windowId: string, targetGroupId: string | null, position: 'above' | 'below' | 'inside') => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    const sourceWindow = state.windows[windowId];
    if (!sourceWindow) {
      log.error(`[SpaceStore] Cannot move window ${windowId} - not found`);
      return;
    }

    log.log(`[SpaceStore] Moving window ${windowId} to group ${targetGroupId} at position ${position}`);

    try {
      if (position === 'inside' && targetGroupId) {
        const targetWindow = state.windows[targetGroupId];
        const groupEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
          (e: any) => e._id === targetWindow?.entityId
        );
        if (groupEntity?.entityType === 'Group') {
          const currentChildEntities = groupEntity.skeleton?.childEntities || [];
          const newChildEntities = [...currentChildEntities, sourceWindow.entityId].filter(Boolean);

          await entityActions.updateEntity(groupEntity._id, {
            skeleton: { ...groupEntity.skeleton, childEntities: newChildEntities },
          });

          log.log(`[SpaceStore] Successfully moved window ${windowId} into group ${targetGroupId}`);
        }
      } else {
        log.log(`[SpaceStore] Reordering window ${windowId} ${position} ${targetGroupId || 'root'}`);
        await get().saveToBackend();
      }
    } catch (error) {
      log.error(`[SpaceStore] Error moving window ${windowId}:`, error);
    }
  },

  createGroupFromWindows: async (windowIds: string[], groupName?: string) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId || windowIds.length < 2) {
      log.error('[SpaceStore] Cannot create group - need at least 2 windows and valid space');
      return null;
    }

    const finalGroupName = groupName || `New Group ${Date.now()}`;
    log.log(`[SpaceStore] Creating group "${finalGroupName}" from windows:`, windowIds);

    try {
      // Get entityIds from the selected windows — entityId is the reference, no embedding needed
      const entityIds = windowIds
        .map(windowId => state.windows[windowId]?.entityId)
        .filter(Boolean);

      if (entityIds.length === 0) {
        log.error('[SpaceStore] No valid entity references found in selected windows');
        return null;
      }

      const groupEntity = {
        entityType: 'Group',
        name: finalGroupName,
        skeleton: {
          '@type': 'Group',
          groupName: finalGroupName,
          childEntities: entityIds,
          isCollapsed: false,
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const createdGroup = await entityActions.createEntity(groupEntity);
      log.log('[SpaceStore] Group created successfully:', createdGroup);

      const groupId = createdGroup.id || createdGroup._id;
      if (!groupId) {
        log.error('[SpaceStore] No group ID found in response:', createdGroup);
        return null;
      }

      // Group window holds only a reference — entity data lives in the entity registry
      const groupWindow: NormalizedWindowEntity = {
        id: `group-${groupId}`,
        type: 'window',
        appType: 'group',
        title: finalGroupName,
        entityId: groupId,
        entityLlmId: createdGroup.llmId,
        position: { x: 100, y: 100 },
        size: { width: 400, height: 300 },
      };

      get().addWindow(groupWindow);

      // DON'T remove the original windows - they should remain as children of the group
      // The hierarchical document list will handle showing them as children

      log.log(`[SpaceStore] Successfully created group "${finalGroupName}" with ID: ${groupId}`);
      return groupId;
    } catch (error) {
      log.error('[SpaceStore] Error creating group:', error);
      return null;
    }
  },

  ungroupWindows: async (groupId: string) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return false;

    const groupWindow = state.windows[groupId];
    const groupEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
      (e: any) => e._id === groupWindow?.entityId
    );
    if (!groupEntity || groupEntity.entityType !== 'Group') {
      log.error(`[SpaceStore] Cannot ungroup - ${groupId} is not a valid group`);
      return false;
    }

    log.log(`[SpaceStore] Ungrouping group ${groupId}`);

    try {
      const childEntityIds = groupEntity.skeleton?.childEntities || [];
      
      const childWindows: NormalizedWindowEntity[] = [];
      for (const entityId of childEntityIds) {
        try {
          const entity = await entityActions.getEntity(entityId);
          const childWindow: NormalizedWindowEntity = {
            id: `window-${entity._id}`,
            type: 'window',
            appType: entity.entityType === 'File' ? 'docs' : 'entityBrowser',
            title: entity.name,
            entityId: entity._id || entity.id,
            entityLlmId: entity.llmId,
            position: {
              x: groupWindow.position.x + Math.random() * 100,
              y: groupWindow.position.y + Math.random() * 100,
            },
            size: { width: 400, height: 300 },
          };
          childWindows.push(childWindow);
        } catch (error) {
          log.error(`[SpaceStore] Error fetching entity ${entityId} for ungrouping:`, error);
        }
      }

      childWindows.forEach(w => get().addWindow(w));

      await entityActions.softDeleteEntity(groupEntity._id);

      get().removeWindow(groupId);

      log.log(`[SpaceStore] Successfully ungrouped ${childWindows.length} windows from group ${groupId}`);
      return true;
    } catch (error) {
      log.error(`[SpaceStore] Error ungrouping ${groupId}:`, error);
      return false;
    }
  },

  updateWindowOrder: async (windowIds: string[], parentGroupId?: string | null) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    log.log(`[SpaceStore] Updating window order for ${windowIds.length} windows in ${parentGroupId || 'root'}`);

    try {
      if (parentGroupId) {
        const groupWindow = state.windows[parentGroupId];
        const groupEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
          (e: any) => e._id === groupWindow?.entityId
        );
        if (groupEntity?.entityType === 'Group') {
          const entityIds = windowIds
            .map(windowId => state.windows[windowId]?.entityId)
            .filter(Boolean);

          await entityActions.updateEntity(groupEntity._id, {
            skeleton: { ...groupEntity.skeleton, childEntities: entityIds },
          });
        }
      } else {
        // Update root-level order
        // For now, just save current state to backend
        await get().saveToBackend();
      }

      log.log(`[SpaceStore] Successfully updated window order`);
    } catch (error) {
      log.error(`[SpaceStore] Error updating window order:`, error);
    }
  },

  updateGroupCollapsedState: async (groupId: string, isCollapsed: boolean) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    const groupWindow = state.windows[groupId];
    const groupEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
      (e: any) => e._id === groupWindow?.entityId
    );
    if (!groupEntity || groupEntity.entityType !== 'Group') {
      log.error(`[SpaceStore] Cannot update collapsed state - ${groupId} is not a valid group`);
      return;
    }

    log.log(`[SpaceStore] Updating group ${groupId} collapsed state to ${isCollapsed}`);

    try {
      await entityActions.updateEntity(groupEntity._id, {
        skeleton: { ...groupEntity.skeleton, isCollapsed },
      });
      log.log(`[SpaceStore] Successfully updated group ${groupId} collapsed state`);
    } catch (error) {
      log.error(`[SpaceStore] Error updating group collapsed state:`, error);
    }
  },

  reorderWindowTabs: (orderedIds: string[]) => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId || !state.activeSpace) return;

    // Normalize: drop duplicates and any ids that are not currently root
    // windows. This is the single write-path for windowTabOrder, so every
    // bad array (cross-session race, remote CRDT drift, buggy caller) gets
    // cleaned here before it reaches CRDT or the backend.
    const validIds = new Set(
      Object.values(state.windows)
        .filter(w => w.type === 'window' && !(w as any).isChildWindow)
        .map(w => w.id),
    );
    const normalized = normalizeWindowTabOrder(orderedIds, validIds);

    // Short-circuit no-op writes to avoid waking up remote observers and
    // flushing the backend queue for nothing.
    const current = state.activeSpace.settings?.windowTabOrder;
    if (windowTabOrderEquals(current, normalized)) return;

    set((s) => ({
      activeSpace: s.activeSpace
        ? { ...s.activeSpace, settings: { ...s.activeSpace.settings, windowTabOrder: normalized } }
        : null,
    }));

    spaceCRDTService.updateSettings(spaceId, { windowTabOrder: normalized });

    backendSaveQueue.schedule(300);
  },

  // ✅ SURGICAL FIX: New normalized entity access methods
  getWindowEntity: (windowId: string) => {
    const state = get();
    const window = state.windows[windowId];
    if (!window) return null;

    // Short-circuit for "New Window" placeholder slots — they use a Window-typed entity
    // as a placeholder that is never meaningful for context or entity lookup.
    // Skipping the entity store search + REST fallback avoids a 553ms blocking fetch on load.
    if ((window.applicationState as any)?.isNew === true) {
      return null;
    }

    // the entity registry is the single source of truth — no embedded entity on windows
    const entities = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]);
    
    // Get applicationState entityId early (needed for multiple checks)
    const appStateEntityId = getWindowEntityId(window);
    
    // PERFORMANCE: Some windows don't have entities (EntityBrowser, etc.) - this is normal
    // Only look up entities for windows that should have them
    if (!window.entityId && !window.entityLlmId && !appStateEntityId) {
      return null; // Silently return null for windows without entity references
    }

    // If the entity registry hasn't finished loading yet, the entity simply isn't available yet.
    // Return null silently — the caller will re-evaluate once the registry initializes.
    // This check must come BEFORE the entity lookup so we don't log ❌ errors for
    // a perfectly normal transient state that resolves on its own.
    if (!(useGlobalEntityStore.getState().lifecycle === "ready")) {
      log.log('[getWindowEntity] entity store not yet initialized, deferring lookup', { windowId, appStateEntityId });
      return null;
    }

    // Find entity by entityId first (most precise), then llmId (which can have collisions)
    // ⚠️ CRITICAL: entityId (_id) is unique and reliable
    // ⚠️ WARNING: llmId can have collisions due to generator bugs!
    let entity = null;
    
    // Try entityId first (MOST RELIABLE - unique database ID)
    if (window.entityId) {
      entity = entities.find((e: any) => e._id === window.entityId || e.id === window.entityId);
      if (entity) {
        return entity;
      }
    }
    
    // 🔧 FIX: Try applicationState entityId (activeDocId, entityId, etc.)
    // This handles cases where window.entityId is not set but applicationState has it
    if (appStateEntityId) {
      entity = entities.find((e: any) => e._id === appStateEntityId || e.id === appStateEntityId);
      if (entity) {
        // ⚠️ VALIDATION: If window.entityLlmId is also set, verify they match
        if (window.entityLlmId && entity.llmId !== window.entityLlmId) {
          console.warn('[getWindowEntity] ⚠️ Mismatch detected - window.entityLlmId does not match entity found via applicationState!', {
            windowId,
            windowEntityLlmId: window.entityLlmId,
            appStateEntityId,
            foundEntityLlmId: entity.llmId,
            foundEntityName: entity.name,
            note: 'Using entity from applicationState (more reliable)'
          });
        }
        return entity;
      }
    }
    
    // Fall back to llmId (WARNING: can have collisions!)
    if (window.entityLlmId) {
      const matchingEntities = entities.filter((e: any) => e.llmId === window.entityLlmId);
      
      if (matchingEntities.length > 1) {
        console.error('[getWindowEntity] 🚨 llmId COLLISION detected! Cannot reliably determine entity!', {
          llmId: window.entityLlmId,
          collisionCount: matchingEntities.length,
          windowId,
          appStateEntityId,
          entities: matchingEntities.map((e: any) => ({
            _id: e._id,
            name: e.name,
            entityType: e.entityType
          }))
        });
        
        // 🔧 FIX: If we have appStateEntityId but didn't find it in all entities above, 
        // try to match it against the collision set (entity might have wrong llmId)
        if (appStateEntityId) {
          const matchFromAppState = matchingEntities.find((e: any) => e._id === appStateEntityId || e.id === appStateEntityId);
          if (matchFromAppState) {
            log.log('[getWindowEntity] ✅ Resolved collision using applicationState entityId:', {
              windowId,
              entityId: appStateEntityId,
              entityName: matchFromAppState.name
            });
            return matchFromAppState;
          }
        }
        
        // 🚨 CRITICAL: Collision and no way to resolve - return null to prevent wrong entity
        console.error('[getWindowEntity] ❌ Cannot resolve llmId collision - returning null to prevent wrong entity!');
        return null;
      }
      
      entity = matchingEntities[0];
      if (entity) {
        return entity;
      }
    }
    
    // Last resort: check entityByIdCache, which is always populated by upsertEntity
    // even when the space entity store has no cache for the entity's spaceId.
    // This handles the common case where an entity is opened before its space cache
    // is loaded (e.g. clicking a generated image in chat before the space cache warms up).
    const entityId = window.entityId || appStateEntityId;
    if (entityId) {
      const cached = useEntityByIdCache.getState().get(entityId);
      if (cached) return cached;
    }

    // Only warn for entity-backed windows (Doc, Note, etc.) — not for app-type windows
    // where entityId is optional context (e.g. selected mail thread) and may not exist yet.
    if (!window.appType) {
      console.warn('[getWindowEntity] Entity not found', {
        windowId,
        windowEntityId: window.entityId,
      });
    }
    
    return null;
  },

  getWindowWithEntity: (windowId: string) => {
    const window = get().windows[windowId];
    if (!window) return null;
    
    // ✅ REFACTOR: Use getWindowEntity helper (which now reads from the entity registry)
    const entity = get().getWindowEntity(windowId);
    return {
      ...window,
      entity
    } as AnyWindowEntity;
  },

  getAllWindowsWithEntities: () => {
    const state = get();
    const windowsWithEntities: Record<string, AnyWindowEntity> = {};
    
    // ✅ REFACTOR: Use getWindowEntity helper for each window
    for (const [windowId, window] of Object.entries(state.windows)) {
      const entity = get().getWindowEntity(windowId);
      windowsWithEntities[windowId] = {
        ...window,
        entity
      } as AnyWindowEntity;
    }
    
    return windowsWithEntities;
  },

  // ✅ REFACTOR: setEntity removed - the entity registry is the single source of truth
  // Components read entities directly from the entity registry via helper methods
  // No manual sync needed between stores

  // ✅ NEW: Centralized entity update that syncs everywhere
  updateEntityEverywhere: async (entityId: string, updates: Partial<any>) => {
    try {
      // 1. Update backend first (source of truth)
      const { entityService } = await import('../services/entityService');
      
      // Add error handling for backend update
      let updatedEntity;
      try {
        updatedEntity = await entityService.update(entityId, updates);
      } catch (backendError) {
        log.error('[SpaceStore] Backend update failed:', { entityId, error: backendError });
        // If backend fails, try to continue with local updates only
        throw new Error(`Backend update failed: ${backendError instanceof Error ? backendError.message : 'Unknown error'}`);
      }

      // 2. Update EntityStore via the canonical pipeline (normalization,
      //    connectedIntegrations, etc. are applied automatically).
      upsertEntity(updatedEntity, {
        source: 'local',
        log: false,
        syncCRDT: false,
      });

      // Real-time sync handled by entity-system Y.Doc (broadcast from REST handler).

      // 5. Emit entity updated event to propagate across all stores
      const state = get();
      const { entityEventService } = await import('../services/entityEventService');
      entityEventService.entityUpdated(
        updatedEntity,
        state.activeSpace?.id || '',
        {
          command: 'updateEntityEverywhere'
        }
      );
      log.info('[SpaceStore] ✅ Emitted entity updated event');

      return updatedEntity;
    } catch (error) {
      log.error('[SpaceStore] updateEntityEverywhere failed:', error);
      throw error;
    }
  },

  // ✅ NEW: Fix broken entity associations for windows
  fixWindowEntityAssociation: async (windowId: string) => {
    const state = get();
    const window = state.windows[windowId];
    
    if (!window) {
      log.info(`[SpaceStore] Window ${windowId} doesn't exist`);
      return;
    }
    
    // ✅ REFACTOR: Check if window has entityLlmId and entity exists in any backing store
    if (window.entityLlmId) {
      const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find((e: any) => e.llmId === window.entityLlmId);
      if (entity) {
        log.info(`[SpaceStore] Window ${windowId} already has valid entity association`);
        return;
      } else {
        log.warn(`[SpaceStore] Window ${windowId} has entityLlmId but entity is missing from registry - will re-fetch`);
        // Continue to re-fetch the missing entity
      }
    }
    
    // Try to find entity by document ID from window OR existing entityLlmId
    const { getWindowEntityId } = await import('../components/Space/utils/windowUtils');
    const docId = getWindowEntityId(window);
    const entityLlmId = window.entityLlmId;
    
    if (!docId && !entityLlmId) {
      log.warn(`[SpaceStore] No entityId or entityLlmId found for window ${windowId}:`, { 
        windowEntityId: window.entityId,
        applicationState: window.applicationState,
        entityLlmId: window.entityLlmId 
      });
      return;
    }
    
    log.info(formatJSON({ windowId, docId, entityLlmId }, '[SpaceStore] 🔧 Fixing entity association for window'));
    
    try {
      // Check if entity already exists in any backing store
      const existingEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find((entity: any) =>
        (docId && (entity._id === docId || entity.id === docId)) ||
        (entityLlmId && entity.llmId === entityLlmId),
      );
      
      if (existingEntity && existingEntity.llmId) {
        
        // Update window with entity reference
        const updatedWindow = {
          ...window,
          entityId: existingEntity._id || existingEntity.id,
          entityLlmId: existingEntity.llmId
        };
        
        set((state) => ({
          windows: {
            ...state.windows,
            [windowId]: updatedWindow
          }
        }));
        
        log.info(formatJSON({
          windowId,
          entityLlmId: existingEntity.llmId,
          entityName: existingEntity.name
        }, '[SpaceStore] ✅ Fixed entity association using entity registry'));
      } else {
        // ✅ FALLBACK: Try direct backend fetch if EntityStore is empty
        // Priority: docId (actual _id) > entityId (window reference) > entityLlmId (last resort)
        const fetchId = docId || window.entityId || entityLlmId;
        if (!fetchId) {
          log.warn(`[SpaceStore] No valid ID to fetch entity for window ${windowId}`);
          return;
        }
        
        const fetchType = docId ? 'docId' : window.entityId ? 'entityId' : 'entityLlmId';
        log.info(`[SpaceStore] Entity not found in stores, trying direct backend fetch for ${fetchType}: ${fetchId}`);
        try {
          // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
          const backendEntity = await entityActions.getEntity(fetchId);
          if (backendEntity && backendEntity.llmId) {
            // ✅ REFACTOR: No need to store in spaceStore - the entity registry is the single source of truth
            
            // Update window with entity reference
            const updatedWindow = {
              ...window,
              entityId: backendEntity._id || backendEntity.id,
              entityLlmId: backendEntity.llmId
            };
            
            set((state) => ({
              windows: {
                ...state.windows,
                [windowId]: updatedWindow
              }
            }));
            
            log.info(formatJSON({
              windowId,
              entityLlmId: backendEntity.llmId,
              entityName: backendEntity.name
            }, '[SpaceStore] ✅ Fixed entity association using direct backend fetch'));
          } else {
            log.warn(`[SpaceStore] ❌ Entity ${docId} not found in backend or missing llmId`);
          }
        } catch (backendError) {
          log.warn(`[SpaceStore] ❌ Could not fetch entity ${fetchId} from backend:`, backendError);
          
          // ✅ CONSERVATIVE: Don't delete windows on 404 - they might just have stale references
          // Only log the error and continue - let user manually clean up if needed
          log.warn(`[SpaceStore] ⚠️ Could not fetch entity for window ${windowId}, but keeping window (entity: ${fetchId})`);
          
          // TODO: In the future, we could mark these windows as "needs attention" in the UI
          // rather than deleting them automatically
        }
      }
    } catch (error) {
      log.error(`[SpaceStore] Error fixing entity association for window ${windowId}:`, error);
    }
  },

  // ✅ NEW: Fix all windows with missing entity associations
  fixAllWindowEntityAssociations: async () => {
    const state = get();
    const windowsToFix = Object.keys(state.windows).filter(windowId => {
      const window = state.windows[windowId];
      return window && !window.entityLlmId && (
        window.applicationState?.docs?.activeDocId ||
        window.applicationState?.pdfium?.entityId ||
        window.applicationState?.notes?.activeNoteId
      );
    });
    
    log.info(`[SpaceStore] 🔧 Fixing entity associations for ${windowsToFix.length} windows:`, windowsToFix);
    
    for (const windowId of windowsToFix) {
      await get().fixWindowEntityAssociation(windowId);
    }
    
    log.info(`[SpaceStore] ✅ Completed fixing entity associations for all windows`);
  },

  // ✅ NEW: Context management methods
  addToContext: (llmId: string, isManuallySet: boolean = false) => {
    const state = get();
    log.log('[SpaceStore] ===== ADD TO CONTEXT START =====');
    log.log('[SpaceStore] Adding entity to persistent context:', { llmId, isManuallySet });
    
    // Check if already in context
    if (state.contextEntities[llmId]) {
      log.log('[SpaceStore] ⚠️ Entity already in context, updating timestamp:', llmId);
    } else {
      log.log('[SpaceStore] ✅ Adding new entity to context:', llmId);
    }
    
    set((state) => ({
      contextEntities: {
        ...state.contextEntities,
        [llmId]: {
          llmId,
          isManuallySet,
          addedAt: Date.now()
        }
      }
    }));
    
    log.log('[SpaceStore] ✅ Successfully added to contextEntities');
    log.log('[SpaceStore] Current context entities count:', Object.keys(get().contextEntities).length);
    log.log('[SpaceStore] ===== ADD TO CONTEXT END =====');
    
    // Schedule backend save to persist context state
    backendSaveQueue.schedule(500);
  },

  removeFromContext: (llmId: string) => {
    const state = get();
    log.info('[SpaceStore] Removing entity from persistent context:', { llmId });
    
    set((state) => {
      const { [llmId]: removed, ...remainingContext } = state.contextEntities;
      return { contextEntities: remainingContext };
    });
    
    // Schedule backend save to persist context state
    backendSaveQueue.schedule(500);
  },

  getContextEntities: () => {
    return get().contextEntities;
  },

  syncContextWithContextStore: async () => {
    const state = get();
    
    try {
      // Import contextStore dynamically to avoid circular dependencies
      const { useContextStore } = await import('../components/Space/SpaceChat/store/contextStore');
      const contextStore = useContextStore.getState();
      
      // Get current context from contextStore
      const currentContext = contextStore.context;
      const newContextEntities: Record<string, { llmId: string; isManuallySet: boolean; addedAt: number }> = {};
      
      // Sync from contextStore entityRefs (keys are llmIds now)
      Object.entries(((currentContext as any).entityRefs) || {}).forEach(([llmId, content]) => {
        if (llmId && (content as any).entityLlmId) {
          newContextEntities[llmId] = {
            llmId: llmId,
            isManuallySet: (content as any).isManuallySet || false,
            addedAt: Date.now()
          };
        }
      });
      
      set({ contextEntities: newContextEntities });
      
      // Schedule backend save
      backendSaveQueue.schedule(500);
    } catch (error) {
      log.error('[SpaceStore] Error syncing context with contextStore:', error);
    }
  },

  // Excerpt persistence methods
  addExcerpt: (excerpt: any) => {
    set((state) => ({ contextExcerpts: [...(state.contextExcerpts || []), excerpt] }));
    backendSaveQueue.schedule(500);
  },
  removeExcerpt: (index: number) => {
    set((state) => ({ contextExcerpts: (state.contextExcerpts || []).filter((_, i) => i !== index) }));
    backendSaveQueue.schedule(500);
  },
  clearExcerpts: () => {
    set({ contextExcerpts: [] });
    backendSaveQueue.schedule(500);
  },
  
  removeContextEntity: (llmId: string) => {
    log.info(`[SpaceStore] Removing context entity: ${llmId}`);
    set((state) => {
      const { [llmId]: removed, ...remainingContextEntities } = state.contextEntities;
      return { contextEntities: remainingContextEntities };
    });
    backendSaveQueue.schedule(500);
  },

  // ✅ NEW: Clean up stale window references
  cleanupStaleWindows: async () => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    log.info('[SpaceStore] 🧹 Cleaning up stale window references');
    
    const validWindows: Record<string, NormalizedWindowEntity> = {};
    const staleWindowIds: string[] = [];
    const orphanedChildWindowIds: string[] = [];
    
    // First pass: Check each window to see if its entity actually exists
    for (const [windowId, window] of Object.entries(state.windows)) {
      const docId = window.applicationState?.docs?.activeDocId || 
                   window.applicationState?.pdfium?.entityId ||
                   window.applicationState?.notes?.activeNoteId;
      
      if (docId) {
        try {
          // ✅ USE ENTITY ACTIONS: Fetch entity data using proper store
          await entityActions.getEntity(docId);
          validWindows[windowId] = window;
          log.info(`[SpaceStore] ✅ Window ${windowId} has valid entity ${docId}`);
        } catch (error) {
          // Entity doesn't exist or can't be fetched
          staleWindowIds.push(windowId);
          log.warn(`[SpaceStore] ❌ Window ${windowId} has stale entity reference ${docId}:`, error);
        }
      } else {
        // Window without entity reference - keep it
        validWindows[windowId] = window;
      }
    }
    
    // Second pass: Check for orphaned child windows (parent doesn't exist)
    for (const [windowId, window] of Object.entries(state.windows)) {
      if (window.isChildWindow && window.parentWindowId) {
        const parentExists = state.windows[window.parentWindowId] !== undefined;
        if (!parentExists) {
          orphanedChildWindowIds.push(windowId);
          log.warn(`[SpaceStore] ❌ Window ${windowId} is orphaned (parent ${window.parentWindowId} doesn't exist)`);
        }
      }
    }
    
    // Combine all windows to remove
    const allWindowsToRemove = [...new Set([...staleWindowIds, ...orphanedChildWindowIds])];
    
    if (allWindowsToRemove.length > 0) {
      log.info(`[SpaceStore] 🗑️ Removing ${allWindowsToRemove.length} windows:`, {
        stale: staleWindowIds,
        orphaned: orphanedChildWindowIds,
        total: allWindowsToRemove
      });
      
      // ✅ FIX: Use helper instead of direct state mutation
      allWindowsToRemove.forEach(windowId => {
        get()._applyLocalWindowDelete(windowId, {
          source: 'local',
          syncCRDT: true,
          log: true
        });
      });
      
      // Force immediate backend save
      await backendSaveQueue.flush();
      log.info(`[SpaceStore] ✅ Cleanup complete - removed ${allWindowsToRemove.length} windows (${staleWindowIds.length} stale, ${orphanedChildWindowIds.length} orphaned)`);
    } else {
      log.info('[SpaceStore] ✅ No stale or orphaned windows found');
    }
  },

  // ✅ NEW: Force sync CRDT with current local state (nuclear option)
  forceSyncCRDTWithLocalState: async () => {
    const state = get();
    const spaceId = state.activeSpace?.id;
    if (!spaceId) return;

    log.info('[SpaceStore] 🚀 Force syncing CRDT with local state');
    
    // Get current local windows
    const localWindows = state.windows;
    log.info(`[SpaceStore] Local state has ${Object.keys(localWindows).length} windows`);
    
    // Force update CRDT to match local state exactly
    for (const [windowId, window] of Object.entries(localWindows)) {
      spaceCRDTService.updateWindow(spaceId, windowId, window);
    }
    
    // Force backend save
    await backendSaveQueue.flush();
    
    log.info(`[SpaceStore] ✅ Force sync complete - CRDT now matches local state`);
  },
  
  // Expose internal helpers
  _applyLocalWindowUpdate,
  _applyLocalWindowDelete,
  
  // ✅ PERFORMANCE: Lazy window loading methods
  markWindowAsLoaded: (windowId: string) => {
    const currentSize = get().loadedWindowIds.size;
    const wasAlreadyLoaded = get().loadedWindowIds.has(windowId);
    
    set((state) => {
      const newSet = new Set(state.loadedWindowIds);
      newSet.add(windowId);
      return { loadedWindowIds: newSet };
    });
    
    const newSize = get().loadedWindowIds.size;
    
    if (!wasAlreadyLoaded) {
      const total = Object.keys(get().windows).length;
      log.debug(`[LAZY-LOAD] Window marked for loading: ${windowId.slice(-12)} loaded=${newSize}/${total} (${Math.round((newSize / total) * 100)}%)`);
    }
  },
  
  markWindowsAsLoaded: (windowIds: string[]) => {
    const current = get().loadedWindowIds;
    const missing = windowIds.filter((wid) => !current.has(wid));
    if (missing.length === 0) return;

    set((state) => {
      const newSet = new Set(state.loadedWindowIds);
      for (const wid of missing) newSet.add(wid);
      return { loadedWindowIds: newSet };
    });

    const total = Object.keys(get().windows).length;
    log.debug(`[LAZY-LOAD] Bulk-loaded ${missing.length} window(s): loaded=${get().loadedWindowIds.size}/${total}`);
  },

  getLoadedWindowIds: () => {
    return get().loadedWindowIds;
  },
  
  // Mount actions
  actions: createSpaceActions(set, get),
}});

export type { AnyWindowEntity, WindowEntity };
export default useSpaceStore;

const createUISpace = (id: string): Space => {
	return {
		id: id,
		name: 'New Desktop',
		windows: {},
		windowOrder: [],
		contextEntities: {}, // ✅ NEW: Initialize empty context state
		activeWindowId: null,
		settings: {
			isContentsVisible: false,
			isChatVisible: false, // Default to hidden for new spaces
			viewMode: 'spatial' as const,
			background: { ...DEFAULT_NEW_SPACE_BACKGROUND },
			sidebar: {
				isVisible: false, // Default to collapsed - only open on hover
				width: 250, // ✅ UPDATED: Keep 250px as requested
				mode: 'normal',
			},
			spaceChat: {
				isHistoryOpen: false,
				mode: 'chat',
				model: modelService.getPlanAwareDefaultModel(),
				isContextVisible: false,
				widths: {
					focused: 480, // Default chat width for focused layout (wider for better UX)
					spatial: 320  // Default chat width for spatial layout
				}
			}
		},
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	}
}

// ✅ REMOVED: Duplicate command registration function
// Commands are now registered centrally via commandRegistryStore.initializeAllCommands()
// This eliminates duplicate registrations and ensures single source of truth

